import { Shield, DollarSign, Users, Building2, Pencil, Trash2, Plus, Check, X, Globe, BarChart3, ArrowRight, AlertTriangle, MapPin, ChevronDown, ChevronRight, Boxes, ImagePlus, CalendarDays, Lock, Gavel, Calculator, Handshake, Truck, FileText, Coins, Puzzle, Sun, Zap, Eye, EyeOff, HardDrive } from "lucide-react";
import { UserAvatar } from "@/components/ui/UserAvatar";
import { PageLoader } from "@/components/ui/Spinner";
import { getRoleLabel } from "@/utils/roleLabel";
import { naturalSortKey } from "@/utils/naturalSort";
import { useState, createContext, useContext, Fragment } from "react";
import { createPortal } from "react-dom";
import { useQuery } from "@tanstack/react-query";
import apiClient from "@/api/client";
import {
  useDirectorsWithProjectGrants,
  useCreateProjectGrant,
  useDeleteProjectGrant,
  useDirectorsWithSalaryGrants,
  useCreateSalaryGrant,
  useDeleteSalaryGrant,
  useViewersWithScheduleGrants,
  useCreateScheduleGrant,
  useDeleteScheduleGrant,
  useUsersWithAgreementGrants,
  useCreateAgreementViewGrant,
  useDeleteAgreementViewGrant,
  useAllUsers,
  usePatchUserRole,
  useCompanies,
  useCreateCompany,
  useUpdateCompany,
  useDeleteCompany,
  useUploadCompanyLogo,
  useDeleteCompanyLogo,
  useUsersWithGroupGrants,
  useCreateGroupFinanceGrant,
  useDeleteGroupFinanceGrant,
  useDirectorsWithInternalCostAccess,
  useSetInternalCostAccess,
  useDeleteInternalCostAccess,
  useUsersWithDLCAccess,
  useCreateDLCAccessGrant,
  useDeleteDLCAccessGrant,
  useBranches,
  useCreateBranch,
  useUpdateBranch,
} from "@/features/ceo/hooks/useCEO";
import { useAuthStore } from "@/store/auth";
import type { UserRolePatch, Company, CompanyFormData, InternalCostLevel, Branch, BranchFormData } from "@/features/ceo/api/ceo";

type Tab = "projects" | "salaries" | "schedule" | "agreements" | "roles" | "companies" | "group_finance" | "internal_costs" | "dlc" | "branches";

// ── CEO action confirmation ───────────────────────────────────────────────────

type CEOConfirmFn = (action: () => void, description?: string) => void;
const CEOConfirmContext = createContext<CEOConfirmFn>((fn) => fn());
function useCEOAction(): CEOConfirmFn { return useContext(CEOConfirmContext); }

// ── Data helpers ───────────────────────────────────────────────────────────────

function useAllProjects() {
  return useQuery({
    queryKey: ["ceo-all-projects"],
    queryFn: () =>
      apiClient
        .get<{ data: { id: string; short_name: string; name: string }[] }>("/projects/")
        .then((r) => r.data.data),
  });
}

function personLabel(u: { first_name?: string; last_name?: string; username?: string; name?: string; id: string }) {
  if (u.name) return u.name;
  if (u.first_name || u.last_name) return `${u.first_name ?? ""} ${u.last_name ?? ""}`.trim();
  return u.username ?? u.id;
}

// ── Project access section ─────────────────────────────────────────────────────

function ProjectAccessSection() {
  const { data: directors = [], isLoading } = useDirectorsWithProjectGrants();
  const { data: projects = [], isLoading: projectsLoading } = useAllProjects();
  const { mutate: createGrant } = useCreateProjectGrant();
  const { mutate: deleteGrant } = useDeleteProjectGrant();
  const ceoConfirm = useCEOAction();
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  if (isLoading || projectsLoading) {
    return <PageLoader />;
  }
  if (directors.length === 0) {
    return <div className="text-gray-400 text-sm py-8 text-center">Brak dyrektorów w systemie.</div>;
  }

  return (
    <div className="flex flex-col gap-2">
      <p className="text-sm text-gray-500 dark:text-gray-400">
        Zaznacz, które przedsięwzięcia są widoczne finansowo dla każdego dyrektora.
        Dotyczy: faktur PRZ, faktur P-GK, planowanych przychodów i analizy finansowej.
      </p>

      {directors.map((row) => {
        const grantedMap = new Map(row.project_grants.map((g) => [g.project_id, g.grant_id]));
        const activeProjectIds = new Set(projects.map((p) => p.id));
        const deletedGrants = row.project_grants.filter((g) => !activeProjectIds.has(g.project_id));
        const totalCount = projects.length + deletedGrants.length;
        const isOpen = !!expanded[row.director.id];

        return (
          <div
            key={row.director.id}
            className="bg-white dark:bg-gray-800 rounded-lg border border-gray-100 dark:border-gray-700/50"
          >
            <button
              type="button"
              onClick={() => setExpanded((prev) => ({ ...prev, [row.director.id]: !prev[row.director.id] }))}
              className="w-full flex items-center gap-2 p-4 text-left hover:bg-gray-50 dark:hover:bg-gray-700/40 rounded-lg transition-colors"
            >
              {isOpen ? (
                <ChevronDown size={16} className="text-gray-400 flex-shrink-0" />
              ) : (
                <ChevronRight size={16} className="text-gray-400 flex-shrink-0" />
              )}
              <UserAvatar
                userId={row.director.id}
                fullName={personLabel(row.director)}
                profilePictureUrl={row.director.profile_picture_url}
                size="sm"
              />
              <span className="font-semibold text-sm text-gray-800 dark:text-gray-200">
                {personLabel(row.director)}
              </span>
              {row.director.is_ceo ? (
                <span className="text-xs bg-[var(--color-primary-dim)] dark:bg-[var(--color-primary-hover)] text-[var(--color-primary)] dark:text-[var(--color-primary)] rounded px-1.5 py-0.5 font-medium">
                  CEO — pełny dostęp
                </span>
              ) : (
                <span className="text-xs text-gray-400">
                  {grantedMap.size} / {totalCount} przedsięwzięć
                </span>
              )}
            </button>

            {isOpen && (
              projects.length === 0 && deletedGrants.length === 0 ? (
                <p className="text-xs text-gray-400 italic px-4 pb-4">Brak przedsięwzięć w systemie.</p>
              ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 px-4 pb-4">
                {projects.slice().sort((a, b) => naturalSortKey(a.short_name).localeCompare(naturalSortKey(b.short_name), "pl")).map((project) => {
                  const grantId = grantedMap.get(project.id);
                  const checked = row.director.is_ceo || !!grantId;
                  const locked = row.director.is_ceo;

                  return (
                    <label
                      key={project.id}
                      title={locked ? "CEO ma dostęp do wszystkich przedsięwzięć" : undefined}
                      className={`flex items-center gap-2 rounded-lg px-2.5 py-1.5 select-none transition-colors ${
                        locked
                          ? "cursor-not-allowed opacity-60"
                          : "cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={locked}
                        onChange={() => {
                          if (checked && grantId)
                            ceoConfirm(() => deleteGrant(grantId), `Cofnięcie dostępu ${personLabel(row.director)} do ${project.short_name}`);
                          else
                            ceoConfirm(() => createGrant({ director: row.director.id, project: project.id }), `Dodanie dostępu ${personLabel(row.director)} do ${project.short_name}`);
                        }}
                        className="w-4 h-4 rounded accent-[var(--color-primary)] cursor-pointer disabled:cursor-not-allowed"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300 truncate" title={project.name}>
                        {project.short_name}
                      </span>
                    </label>
                  );
                })}
                {deletedGrants.map((grant) => {
                  const shortName = grant.project_name.split(" — ")[0] || grant.project_name;
                  const grantId = grant.grant_id;
                  return (
                    <label
                      key={grant.project_id}
                      title={`${grant.project_name} — przedsięwzięcie usunięte. Kliknij aby cofnąć dostęp.`}
                      className="flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 select-none transition-colors cursor-pointer hover:bg-red-50 dark:hover:bg-red-950/20 opacity-60"
                    >
                      <input
                        type="checkbox"
                        checked={true}
                        onChange={() => ceoConfirm(() => deleteGrant(grantId), `Cofnięcie dostępu ${personLabel(row.director)} do usuniętego przedsięwzięcia`)}
                        className="w-4 h-4 rounded accent-[var(--color-primary)] cursor-pointer"
                      />
                      <span className="text-sm text-gray-400 dark:text-gray-500 line-through truncate">
                        {shortName}
                      </span>
                      <span className="text-[10px] leading-none bg-red-100 dark:bg-red-900/40 text-red-500 dark:text-red-400 rounded px-1 py-0.5 font-medium shrink-0">
                        usunięte
                      </span>
                    </label>
                  );
                })}
              </div>
              )
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Salary access section ──────────────────────────────────────────────────────

function SalaryAccessSection() {
  const { data, isLoading } = useDirectorsWithSalaryGrants();
  const { mutate: createGrant } = useCreateSalaryGrant();
  const { mutate: deleteGrant } = useDeleteSalaryGrant();

  const directors = data?.data ?? [];
  const employees = data?.employees ?? [];
  const ceoConfirm = useCEOAction();

  if (isLoading) {
    return <PageLoader />;
  }
  if (directors.length === 0) {
    return <div className="text-gray-400 text-sm py-8 text-center">Brak dyrektorów w systemie.</div>;
  }

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-gray-500 dark:text-gray-400">
        Zaznacz, których pracowników koszty płac są widoczne dla każdego dyrektora.
        Dotyczy: kosztów płac, alokacji pracowników i faktur kosztowych.
      </p>

      {directors.map((row) => {
        const grantedMap = new Map(row.employee_grants.map((g) => [g.employee_id, g.grant_id]));
        const dirBranchId = (row.director.branch as { id: string } | null)?.id ?? null;
        const sameBranch = employees.filter((e) => dirBranchId && e.branch_id === dirBranchId);
        const otherBranch = employees.filter((e) => !dirBranchId || e.branch_id !== dirBranchId);

        const EmpLabel = ({ emp }: { emp: (typeof employees)[0] }) => {
          const grantId = grantedMap.get(emp.id);
          const checked = !!grantId;
          return (
            <label className="flex items-center gap-2 px-2.5 py-1.5 select-none cursor-pointer hover:bg-black/5 dark:hover:bg-white/5 rounded transition-colors">
              <input
                type="checkbox"
                checked={checked}
                onChange={() => {
                  if (checked && grantId)
                    ceoConfirm(() => deleteGrant(grantId), `Cofnięcie dostępu ${personLabel(row.director)} do płac ${emp.name}`);
                  else
                    ceoConfirm(() => createGrant({ director: row.director.id, employee: emp.id }), `Dodanie dostępu ${personLabel(row.director)} do płac ${emp.name}`);
                }}
                className="w-4 h-4 rounded accent-green-600 cursor-pointer flex-shrink-0"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300 truncate" title={emp.name}>
                {emp.name}
              </span>
            </label>
          );
        };

        return (
          <div key={row.director.id} className="bg-white dark:bg-gray-800 p-4">
            <div className="flex items-center gap-2 mb-3">
              <UserAvatar
                userId={row.director.id}
                fullName={personLabel(row.director)}
                profilePictureUrl={row.director.profile_picture_url}
                size="sm"
              />
              <span className="font-semibold text-sm text-gray-800 dark:text-gray-200">
                {personLabel(row.director)}
              </span>
              {row.director.is_ceo ? (
                <span className="text-xs bg-[var(--color-primary-dim)] dark:bg-[var(--color-primary-hover)] text-[var(--color-primary)] rounded px-1.5 py-0.5 font-medium">
                  CEO — pełny dostęp
                </span>
              ) : (
                <span className="text-xs text-gray-400">
                  {grantedMap.size} / {employees.length} pracowników
                </span>
              )}
            </div>

            {employees.length === 0 ? (
              <p className="text-xs text-gray-400 italic">Brak pracowników w systemie.</p>
            ) : row.director.is_ceo ? (
              <p className="text-xs text-gray-400 italic">Pełny dostęp do wszystkich pracowników.</p>
            ) : (
              <div className="flex flex-col gap-3">
                {sameBranch.length > 0 && (
                  <div className="rounded-md border border-blue-200 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-900/10 px-3 pt-2 pb-1">
                    <p className="text-[10px] font-semibold uppercase tracking-widest text-blue-500 dark:text-blue-400 mb-1">
                      Oddział {row.director.branch?.name}
                    </p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4">
                      {sameBranch.map((emp) => <EmpLabel key={emp.id} emp={emp} />)}
                    </div>
                  </div>
                )}
                {otherBranch.length > 0 && (
                  <div className="px-1">
                    {dirBranchId && (
                      <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500 mb-1 px-1.5">
                        Inne oddziały
                      </p>
                    )}
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4">
                      {otherBranch.map((emp) => <EmpLabel key={emp.id} emp={emp} />)}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Schedule view access section ───────────────────────────────────────────────

function ScheduleAccessSection() {
  const { data, isLoading } = useViewersWithScheduleGrants();
  const { mutate: createGrant } = useCreateScheduleGrant();
  const { mutate: deleteGrant } = useDeleteScheduleGrant();
  const ceoConfirm = useCEOAction();

  const rows = data?.data ?? [];
  const allUsers = data?.users ?? [];
  const [draftViewerIds, setDraftViewerIds] = useState<string[]>([]);
  const [pickerValue, setPickerValue] = useState("");

  if (isLoading) {
    return <PageLoader />;
  }

  const serverViewerIds = new Set(rows.map((r) => r.viewer.id));
  const userById = new Map(allUsers.map((u) => [u.id, u]));

  type ViewerRow = {
    id: string;
    name: string;
    profilePictureUrl?: string | null;
    branchId: string | null;
    branchName: string | null;
    grants: { grant_id: string; employee_id: string; employee_name: string }[];
    isDraft: boolean;
  };

  const viewerRows: ViewerRow[] = [
    ...rows.map((r) => ({
      id: r.viewer.id,
      name: personLabel(r.viewer),
      profilePictureUrl: r.viewer.profile_picture_url,
      branchId: r.viewer.branch?.id ?? null,
      branchName: r.viewer.branch?.name ?? null,
      grants: r.employee_grants,
      isDraft: false,
    })),
    ...draftViewerIds
      .filter((id) => !serverViewerIds.has(id))
      .map((id) => {
        const u = userById.get(id);
        return {
          id,
          name: u?.name ?? id,
          profilePictureUrl: null,
          branchId: u?.branch_id ?? null,
          branchName: u?.branch_name ?? null,
          grants: [],
          isDraft: true,
        };
      }),
  ];

  const pickableViewers = allUsers.filter(
    (u) => !serverViewerIds.has(u.id) && !draftViewerIds.includes(u.id)
  );

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-gray-500 dark:text-gray-400">
        Nadaj wybranej osobie wgląd w grafik dyspozycyjności pracowników spoza jej oddziału
        (np. dyrektorowi z Wrocławia — wgląd w grafik osób z Opola i pracowników zdalnych).
        Osoby z własnego oddziału są widoczne automatycznie. Wgląd jest tylko do odczytu —
        nie daje prawa akceptowania wpisów.
      </p>

      <div className="flex items-center gap-2">
        <select
          value={pickerValue}
          onChange={(e) => setPickerValue(e.target.value)}
          className="text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-gray-700 dark:text-gray-300 max-w-xs"
        >
          <option value="">— wybierz osobę oglądającą —</option>
          {pickableViewers.map((u) => (
            <option key={u.id} value={u.id}>
              {u.name}{u.branch_name ? ` (${u.branch_name})` : ""}
            </option>
          ))}
        </select>
        <button
          type="button"
          disabled={!pickerValue}
          onClick={() => {
            if (!pickerValue) return;
            setDraftViewerIds((prev) => [...prev, pickerValue]);
            setPickerValue("");
          }}
          className="flex items-center gap-1.5 text-sm font-medium rounded-lg px-3 py-2 bg-[var(--color-primary)] text-white disabled:opacity-40 disabled:cursor-not-allowed hover:opacity-90 transition-opacity"
        >
          <Plus size={14} /> Dodaj wgląd
        </button>
      </div>

      {viewerRows.length === 0 ? (
        <div className="text-gray-400 text-sm py-8 text-center">
          Nikt nie ma jeszcze nadanego wglądu. Wybierz osobę powyżej, aby zacząć.
        </div>
      ) : (
        viewerRows.map((row) => {
          const grantedMap = new Map(row.grants.map((g) => [g.employee_id, g.grant_id]));
          // Same-branch people are already visible through branch scoping — offer only others.
          const pickableEmployees = allUsers.filter(
            (u) => u.id !== row.id && (!row.branchId || u.branch_id !== row.branchId)
          );
          const byBranch = new Map<string, typeof pickableEmployees>();
          for (const emp of pickableEmployees) {
            const key = emp.branch_name ?? "Bez oddziału";
            if (!byBranch.has(key)) byBranch.set(key, []);
            byBranch.get(key)!.push(emp);
          }

          return (
            <div key={row.id} className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-100 dark:border-gray-700/50">
              <div className="flex items-center gap-2 mb-3">
                <UserAvatar userId={row.id} fullName={row.name} profilePictureUrl={row.profilePictureUrl} size="sm" />
                <span className="font-semibold text-sm text-gray-800 dark:text-gray-200">{row.name}</span>
                {row.branchName && <span className="text-xs text-gray-400">{row.branchName}</span>}
                <span className="text-xs text-gray-400">
                  wgląd w grafik {grantedMap.size} {grantedMap.size === 1 ? "osoby" : "osób"}
                </span>
                {row.isDraft && (
                  <button
                    type="button"
                    onClick={() => setDraftViewerIds((prev) => prev.filter((id) => id !== row.id))}
                    className="ml-auto text-gray-400 hover:text-red-500 transition-colors"
                    title="Usuń z listy (bez zapisanych grantów)"
                  >
                    <X size={16} />
                  </button>
                )}
              </div>

              {pickableEmployees.length === 0 ? (
                <p className="text-xs text-gray-400 italic">Brak osób spoza oddziału tej osoby.</p>
              ) : (
                <div className="flex flex-col gap-3">
                  {[...byBranch.entries()].map(([branchName, emps]) => (
                    <div key={branchName} className="px-1">
                      <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500 mb-1 px-1.5">
                        {branchName}
                      </p>
                      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4">
                        {emps.map((emp) => {
                          const grantId = grantedMap.get(emp.id);
                          const checked = !!grantId;
                          return (
                            <label
                              key={emp.id}
                              className="flex items-center gap-2 px-2.5 py-1.5 select-none cursor-pointer hover:bg-black/5 dark:hover:bg-white/5 rounded transition-colors"
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => {
                                  if (checked && grantId)
                                    ceoConfirm(() => deleteGrant(grantId), `Cofnięcie wglądu ${row.name} w grafik ${emp.name}`);
                                  else
                                    ceoConfirm(() => createGrant({ viewer: row.id, employee: emp.id }), `Nadanie ${row.name} wglądu w grafik ${emp.name}`);
                                }}
                                className="w-4 h-4 rounded accent-[var(--color-primary)] cursor-pointer flex-shrink-0"
                              />
                              <span className="text-sm text-gray-700 dark:text-gray-300 truncate" title={emp.name}>
                                {emp.name}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
}

// ── Confidential agreements access section ─────────────────────────────────────

function AgreementAccessSection() {
  const { data, isLoading } = useUsersWithAgreementGrants();
  const { mutate: createGrant } = useCreateAgreementViewGrant();
  const { mutate: deleteGrant } = useDeleteAgreementViewGrant();
  const ceoConfirm = useCEOAction();

  const rows = data?.data ?? [];
  const allUsers = data?.users ?? [];
  const agreements = data?.agreements ?? [];
  const [draftUserIds, setDraftUserIds] = useState<string[]>([]);
  const [pickerValue, setPickerValue] = useState("");

  if (isLoading) {
    return <PageLoader />;
  }

  const serverUserIds = new Set(rows.map((r) => r.user.id));
  const userById = new Map(allUsers.map((u) => [u.id, u]));

  type UserRow = {
    id: string;
    name: string;
    profilePictureUrl?: string | null;
    role?: string;
    grants: { grant_id: string; agreement_id: string; agreement_title: string; contractor_name: string }[];
    isDraft: boolean;
  };

  const userRows: UserRow[] = [
    ...rows.map((r) => ({
      id: r.user.id,
      name: personLabel(r.user),
      profilePictureUrl: r.user.profile_picture_url,
      role: r.user.role,
      grants: r.agreement_grants,
      isDraft: false,
    })),
    ...draftUserIds
      .filter((id) => !serverUserIds.has(id))
      .map((id) => {
        const u = userById.get(id);
        return { id, name: u?.name ?? id, profilePictureUrl: null, role: u?.role, grants: [], isDraft: true };
      }),
  ];

  const pickableUsers = allUsers.filter(
    (u) => !serverUserIds.has(u.id) && !draftUserIds.includes(u.id)
  );

  const byContractor = new Map<string, typeof agreements>();
  for (const a of agreements) {
    if (!byContractor.has(a.contractor_name)) byContractor.set(a.contractor_name, []);
    byContractor.get(a.contractor_name)!.push(a);
  }

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-gray-500 dark:text-gray-400">
        Umowy ramowe oznaczone jako niejawne widzą tylko dyrektorzy i CEO. Tutaj możesz
        zdjąć tę blokadę punktowo — dla wybranej osoby i konkretnej umowy. Osoba zobaczy
        umowę wraz z cennikiem (stawkami) w panelu Umowy ramowe i na zamówieniach z niej
        wynikających. Dostęp jest tylko do wglądu — edycja umów i cenników nadal wymaga
        uprawnienia „Podwykonawcy i umowy ramowe" (zakładka Role).
      </p>

      <div className="flex items-center gap-2">
        <select
          value={pickerValue}
          onChange={(e) => setPickerValue(e.target.value)}
          className="text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-gray-700 dark:text-gray-300 max-w-xs"
        >
          <option value="">— wybierz osobę —</option>
          {pickableUsers.map((u) => (
            <option key={u.id} value={u.id}>
              {u.name} ({getRoleLabel(u.role)})
            </option>
          ))}
        </select>
        <button
          type="button"
          disabled={!pickerValue}
          onClick={() => {
            if (!pickerValue) return;
            setDraftUserIds((prev) => [...prev, pickerValue]);
            setPickerValue("");
          }}
          className="flex items-center gap-1.5 text-sm font-medium rounded-lg px-3 py-2 bg-[var(--color-primary)] text-white disabled:opacity-40 disabled:cursor-not-allowed hover:opacity-90 transition-opacity"
        >
          <Plus size={14} /> Dodaj dostęp
        </button>
      </div>

      {agreements.length === 0 && (
        <div className="text-gray-400 text-sm py-8 text-center">
          Brak umów niejawnych w systemie — nie ma czego udostępniać.
        </div>
      )}

      {agreements.length > 0 && (userRows.length === 0 ? (
        <div className="text-gray-400 text-sm py-8 text-center">
          Nikt nie ma jeszcze nadanego dostępu. Wybierz osobę powyżej, aby zacząć.
        </div>
      ) : (
        userRows.map((row) => {
          const grantedMap = new Map(row.grants.map((g) => [g.agreement_id, g.grant_id]));
          // Grants on agreements meanwhile made public (or deleted) — still revocable.
          const staleGrants = row.grants.filter((g) => !agreements.some((a) => a.id === g.agreement_id));
          return (
            <div key={row.id} className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-100 dark:border-gray-700/50">
              <div className="flex items-center gap-2 mb-3">
                <UserAvatar userId={row.id} fullName={row.name} profilePictureUrl={row.profilePictureUrl} size="sm" />
                <span className="font-semibold text-sm text-gray-800 dark:text-gray-200">{row.name}</span>
                {row.role && <span className="text-xs text-gray-400">{getRoleLabel(row.role)}</span>}
                <span className="text-xs text-gray-400">
                  {grantedMap.size} {grantedMap.size === 1 ? "umowa" : "umów"}
                </span>
                {row.isDraft && (
                  <button
                    type="button"
                    onClick={() => setDraftUserIds((prev) => prev.filter((id) => id !== row.id))}
                    className="ml-auto text-gray-400 hover:text-red-500 transition-colors"
                    title="Usuń z listy (bez zapisanych dostępów)"
                  >
                    <X size={16} />
                  </button>
                )}
              </div>

              <div className="flex flex-col gap-3">
                {[...byContractor.entries()].map(([contractorName, list]) => (
                  <div key={contractorName} className="px-1">
                    <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500 mb-1 px-1.5">
                      {contractorName}
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
                      {list.map((agreement) => {
                        const grantId = grantedMap.get(agreement.id);
                        const checked = !!grantId;
                        return (
                          <label
                            key={agreement.id}
                            className="flex items-center gap-2 px-2.5 py-1.5 select-none cursor-pointer hover:bg-black/5 dark:hover:bg-white/5 rounded transition-colors"
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => {
                                if (checked && grantId)
                                  ceoConfirm(() => deleteGrant(grantId), `Cofnięcie dostępu ${row.name} do umowy „${agreement.title}"`);
                                else
                                  ceoConfirm(() => createGrant({ user: row.id, agreement: agreement.id }), `Nadanie ${row.name} dostępu do umowy niejawnej „${agreement.title}"`);
                              }}
                              className="w-4 h-4 rounded accent-[var(--color-primary)] cursor-pointer flex-shrink-0"
                            />
                            <span className="text-sm text-gray-700 dark:text-gray-300 truncate" title={agreement.title}>
                              {agreement.title}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                ))}
                {staleGrants.length > 0 && (
                  <div className="px-1">
                    <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500 mb-1 px-1.5">
                      Umowy już nie-niejawne lub usunięte
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
                      {staleGrants.map((g) => (
                        <label
                          key={g.grant_id}
                          title={`${g.contractor_name} — ${g.agreement_title}. Kliknij aby cofnąć dostęp.`}
                          className="flex items-center gap-2 px-2.5 py-1.5 select-none cursor-pointer hover:bg-red-50 dark:hover:bg-red-950/20 rounded transition-colors opacity-60"
                        >
                          <input
                            type="checkbox"
                            checked={true}
                            onChange={() => ceoConfirm(() => deleteGrant(g.grant_id), `Cofnięcie dostępu ${row.name} do umowy „${g.agreement_title}"`)}
                            className="w-4 h-4 rounded accent-[var(--color-primary)] cursor-pointer flex-shrink-0"
                          />
                          <span className="text-sm text-gray-400 dark:text-gray-500 line-through truncate">
                            {g.agreement_title}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })
      ))}
    </div>
  );
}

// ── Group Finance Access section ───────────────────────────────────────────────

function GroupFinanceAccessSection() {
  const { data, isLoading } = useUsersWithGroupGrants();
  const { mutate: createGrant } = useCreateGroupFinanceGrant();
  const { mutate: deleteGrant } = useDeleteGroupFinanceGrant();

  const users = data?.data ?? [];
  const companies = data?.companies ?? [];
  const ceoConfirm = useCEOAction();

  if (isLoading) {
    return <PageLoader />;
  }
  if (users.length === 0) {
    return <div className="text-gray-400 text-sm py-8 text-center">Brak dyrektorów/koordynatorów w systemie.</div>;
  }

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-gray-500 dark:text-gray-400">
        Zaznacz, które spółki GK ELEKTIM są widoczne dla każdego dyrektora / koordynatora
        w panelu finansowym GK. CEO i Księgowy mają automatycznie pełny dostęp.
      </p>

      {users.map((row) => {
        const grantedMap = new Map(row.company_grants.map((g) => [g.company_id, g.grant_id]));

        return (
          <div
            key={row.user.id}
            className="bg-white dark:bg-gray-800 p-4"
          >
            <div className="flex items-center gap-2 mb-3">
              <span className="font-semibold text-sm text-gray-800 dark:text-gray-200">
                {personLabel(row.user)}
              </span>
              <span className="text-xs text-gray-400">{getRoleLabel(row.user.role)}</span>
              {row.user.is_ceo ? (
                <span className="text-xs bg-[var(--color-primary-dim)] dark:bg-[var(--color-primary-hover)] text-[var(--color-primary)] dark:text-[var(--color-primary)] rounded px-1.5 py-0.5 font-medium">
                  CEO — pełny dostęp
                </span>
              ) : (
                <span className="text-xs text-gray-400">
                  {grantedMap.size} / {companies.length} spółek
                </span>
              )}
            </div>

            {companies.length === 0 ? (
              <p className="text-xs text-gray-400 italic">Brak aktywnych spółek. Dodaj je w zakładce Spółki GK.</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {companies.map((company) => {
                  const grantId = grantedMap.get(company.id);
                  const checked = row.user.is_ceo || !!grantId;
                  const locked = row.user.is_ceo;

                  return (
                    <label
                      key={company.id}
                      title={locked ? "CEO ma dostęp do wszystkich spółek" : company.name || undefined}
                      className={`flex items-center gap-2 rounded-lg px-2.5 py-1.5 border select-none transition-colors ${
                        locked
                          ? "cursor-not-allowed opacity-60 border-gray-200 dark:border-gray-700"
                          : checked
                          ? "cursor-pointer border-[var(--color-primary)] bg-[var(--color-primary-dim)] dark:border-[var(--color-primary)] dark:bg-[var(--color-primary-hover)]"
                          : "cursor-pointer border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={locked}
                        onChange={() => {
                          if (checked && grantId)
                            ceoConfirm(() => deleteGrant(grantId), `Cofnięcie dostępu ${personLabel(row.user)} do spółki ${company.short_code}`);
                          else
                            ceoConfirm(() => createGrant({ user: row.user.id, company: company.id }), `Dodanie dostępu ${personLabel(row.user)} do spółki ${company.short_code}`);
                        }}
                        className="w-4 h-4 rounded accent-[var(--color-primary)] cursor-pointer disabled:cursor-not-allowed"
                      />
                      <span className="text-sm font-mono font-medium text-gray-700 dark:text-gray-300">
                        {company.short_code}
                      </span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Internal Cost Access section ──────────────────────────────────────────────

const INTERNAL_COST_LEVELS: { value: InternalCostLevel | "none"; label: string; description: string }[] = [
  { value: "none", label: "Brak dostępu", description: "Dyrektor nie widzi kosztów wewnętrznych ani podatków" },
  { value: "aggregate", label: "Kwoty łączne na wykresie", description: "Tylko sumy kosztów wewnętrznych i podatków widoczne na cashflow" },
  { value: "full", label: "Pełne koszty wewnętrzne i podatki", description: "Wszystkie pozycje kosztów wewnętrznych oraz wpisy VAT/CIT" },
];

function InternalCostAccessSection() {
  const { data: rows = [], isLoading } = useDirectorsWithInternalCostAccess();
  const { mutate: setAccess } = useSetInternalCostAccess();
  const { mutate: deleteAccess } = useDeleteInternalCostAccess();
  const ceoConfirm = useCEOAction();

  if (isLoading) return <PageLoader />;
  if (rows.length === 0) {
    return <div className="text-gray-400 text-sm py-8 text-center">Brak dyrektorów/koordynatorów w systemie.</div>;
  }

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-gray-500 dark:text-gray-400">
        Określ poziom dostępu do kosztów wewnętrznych i podatków (VAT/CIT) dla każdego dyrektora.
        CEO i Księgowy zawsze widzą pełne koszty.
      </p>

      {rows.map((row) => {
        const currentLevel: InternalCostLevel | "none" = row.grant?.level ?? "none";
        const locked = row.director.is_ceo;

        function handleChange(value: InternalCostLevel | "none") {
          if (locked) return;
          const optLabel = INTERNAL_COST_LEVELS.find((o) => o.value === value)?.label ?? value;
          ceoConfirm(
            () => {
              if (value === "none") {
                if (row.grant) deleteAccess(row.grant.id);
              } else {
                setAccess({ director: row.director.id, level: value });
              }
            },
            `Zmiana dostępu ${personLabel(row.director)} do kosztów wewnętrznych → ${optLabel}`,
          );
        }

        return (
          <div
            key={row.director.id}
            className="bg-white dark:bg-gray-800 p-4"
          >
            <div className="flex items-center gap-2 mb-3">
              <span className="font-semibold text-sm text-gray-800 dark:text-gray-200">
                {personLabel(row.director)}
              </span>
              <span className="text-xs text-gray-400">{getRoleLabel(row.director.role)}</span>
              {locked && (
                <span className="text-xs bg-[var(--color-primary-dim)] dark:bg-[var(--color-primary-hover)] text-[var(--color-primary)] dark:text-[var(--color-primary)] rounded px-1.5 py-0.5 font-medium">
                  CEO — pełny dostęp
                </span>
              )}
            </div>

            <div className="flex flex-wrap gap-2">
              {INTERNAL_COST_LEVELS.map((opt) => {
                const isSelected = locked ? opt.value === "full" : opt.value === currentLevel;
                return (
                  <label
                    key={opt.value}
                    title={locked ? "CEO ma zawsze pełny dostęp" : opt.description}
                    className={`flex items-center gap-2 rounded-lg px-3 py-2 border select-none transition-colors text-sm ${
                      locked
                        ? "cursor-not-allowed opacity-60 border-gray-200 dark:border-gray-700"
                        : isSelected
                        ? "cursor-pointer border-orange-300 bg-orange-50 dark:border-orange-700 dark:bg-orange-900/20"
                        : "cursor-pointer border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700"
                    }`}
                  >
                    <input
                      type="radio"
                      name={`internal-cost-${row.director.id}`}
                      checked={isSelected}
                      disabled={locked}
                      onChange={() => handleChange(opt.value)}
                      className="accent-orange-600 disabled:cursor-not-allowed"
                    />
                    <span className={`font-medium ${isSelected && !locked ? "text-orange-700 dark:text-orange-300" : "text-gray-700 dark:text-gray-300"}`}>
                      {opt.label}
                    </span>
                  </label>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── DLC module access section ──────────────────────────────────────────────────

// Per-module checkbox styling — mirrors the DLC badge colours in the sidebar.
const DLC_MODULE_STYLES: Record<string, { badge: string; accent: string }> = {
  dlc_01: { badge: "bg-gradient-to-r from-indigo-500 to-fuchsia-500 text-white", accent: "accent-fuchsia-600" },
  dlc_01_min: { badge: "bg-gradient-to-r from-indigo-300 to-fuchsia-300 text-indigo-950", accent: "accent-fuchsia-400" },
  dlc_02: { badge: "bg-gradient-to-r from-emerald-500 to-teal-500 text-white", accent: "accent-teal-600" },
  dlc_03: { badge: "bg-gradient-to-r from-violet-600 to-rose-500 text-white", accent: "accent-rose-600" },
  dlc_04: { badge: "bg-gradient-to-r from-amber-500 to-orange-600 text-white", accent: "accent-orange-600" },
  dlc_06: { badge: "bg-gradient-to-r from-sky-600 to-cyan-500 text-white", accent: "accent-cyan-600" },
  dlc_07: { badge: "bg-gradient-to-r from-pink-500 to-rose-600 text-white", accent: "accent-rose-600" },
  dlc_09: { badge: "bg-gradient-to-r from-indigo-500 to-violet-500 text-white", accent: "accent-violet-600" },
  dlc_10: { badge: "bg-gradient-to-r from-lime-500 to-emerald-600 text-white", accent: "accent-emerald-600" },
  dlc_12: { badge: "bg-gradient-to-r from-amber-500 to-rose-600 text-white", accent: "accent-rose-600" },
};

function DLCAccessSection() {
  const { data, isLoading } = useUsersWithDLCAccess();
  const { mutate: createGrant } = useCreateDLCAccessGrant();
  const { mutate: deleteGrant } = useDeleteDLCAccessGrant();

  const users = data?.data ?? [];
  const allModules = data?.modules ?? [];
  const contractorModules = new Set(data?.contractorModules ?? []);
  const ceoConfirm = useCEOAction();

  if (isLoading) return <PageLoader />;
  if (users.length === 0) {
    return <div className="text-gray-400 text-sm py-8 text-center">Brak użytkowników w systemie.</div>;
  }

  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm text-gray-500 dark:text-gray-400">
        Zaznacz, kto ma dostęp do poszczególnych modułów DLC. Podwykonawcom można nadać wyłącznie
        DLC_01, DLC_02 i DLC_12 — uwaga: pełny DLC_01 pokazuje cały portfel projektów ZE, nie tylko
        zlecenia firmy. CEO ma automatycznie pełny dostęp do wszystkich modułów.
      </p>

      {users.map((row, idx) => {
        const isContractor = row.user.role === "contractor";
        const modules = isContractor
          ? allModules.filter((m) => contractorModules.has(m.value))
          : allModules;
        const firstContractor = isContractor && users[idx - 1]?.user.role !== "contractor";
        const grantedMap = new Map(row.module_grants.map((g) => [g.module, g.grant_id]));
        // Moduły przysługujące z urzędu (DLC_12 dla dyrektorów technicznych) —
        // haczyk zaznaczony i zamknięty, tak jak u CEO: to reguła roli, nie wpis.
        const autoModules = new Set(row.auto_modules ?? []);

        return (
          <Fragment key={row.user.id}>
          {firstContractor && (
            <h3 className="mt-4 text-sm font-semibold text-gray-700 dark:text-gray-300">Podwykonawcy</h3>
          )}
          <div
            className="bg-white dark:bg-gray-800 rounded-lg border border-gray-100 dark:border-gray-700/50 p-4"
          >
            <div className="flex items-center gap-2 mb-3">
              <UserAvatar
                userId={row.user.id}
                fullName={personLabel(row.user)}
                profilePictureUrl={row.user.profile_picture_url}
                size="sm"
              />
              <span className="font-semibold text-sm text-gray-800 dark:text-gray-200">{personLabel(row.user)}</span>
              <span className="text-xs text-gray-400">
                {getRoleLabel(row.user.role)}
                {isContractor && row.user.contractor_company_name ? ` · ${row.user.contractor_company_name}` : ""}
              </span>
              {row.user.is_ceo ? (
                <span className="text-xs bg-[var(--color-primary-dim)] dark:bg-[var(--color-primary-hover)] text-[var(--color-primary)] dark:text-[var(--color-primary)] rounded px-1.5 py-0.5 font-medium">
                  CEO — pełny dostęp
                </span>
              ) : (
                <span className="text-xs text-gray-400">
                  {grantedMap.size + autoModules.size} / {modules.length} modułów
                </span>
              )}
            </div>

            <div className="flex flex-wrap gap-2">
              {modules.map((mod) => {
                const grantId = grantedMap.get(mod.value);
                const byRole = autoModules.has(mod.value);
                // Dostęp minimalny do DLC_01 (sam widok działek) zawiera się w pełnym
                // DLC_01 — przy pełnym grancie haczyk jest zaznaczony i zamknięty.
                const coveredByFull =
                  mod.value === "dlc_01_min" && grantedMap.has("dlc_01") && !grantId;
                const checked = row.user.is_ceo || byRole || coveredByFull || !!grantId;
                const locked = row.user.is_ceo || byRole || coveredByFull;
                const style = DLC_MODULE_STYLES[mod.value];
                const moduleName = mod.label.replace(/^DLC_\d+\s*[—-]\s*/, "");

                return (
                  <label
                    key={mod.value}
                    title={
                      row.user.is_ceo
                        ? "CEO ma dostęp do wszystkich modułów DLC"
                        : byRole
                          ? `${mod.label} — przysługuje z urzędu (dyrektor techniczny)`
                          : coveredByFull
                            ? `${mod.label} — zawiera się w pełnym dostępie do DLC_01`
                            : mod.value === "dlc_01_min"
                              ? `${mod.label} — tylko zbiorcza lista działek (nazwy projektów i filtry, bez wejścia w projekty i szczegóły)`
                              : mod.label
                    }
                    className={`flex items-center gap-2 rounded-lg px-3 py-2 border select-none transition-colors ${
                      locked
                        ? "cursor-not-allowed opacity-60 border-gray-200 dark:border-gray-700"
                        : checked
                        ? "cursor-pointer border-[var(--color-primary)] bg-[var(--color-primary-dim)] dark:border-[var(--color-primary)] dark:bg-[var(--color-primary-hover)]"
                        : "cursor-pointer border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={locked}
                      onChange={() => {
                        if (checked && grantId)
                          ceoConfirm(() => deleteGrant(grantId), `Cofnięcie dostępu ${personLabel(row.user)} do ${mod.label}`);
                        else
                          ceoConfirm(() => createGrant({ user: row.user.id, module: mod.value }), `Dodanie dostępu ${personLabel(row.user)} do ${mod.label}`);
                      }}
                      className={`w-4 h-4 rounded cursor-pointer disabled:cursor-not-allowed ${style?.accent ?? "accent-[var(--color-primary)]"}`}
                    />
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded shadow-sm ${style?.badge ?? "bg-gray-500 text-white"}`}>
                      {mod.value.toUpperCase().replace(/_MIN$/, " · MIN")}
                    </span>
                    <span className="text-sm text-gray-700 dark:text-gray-300">{moduleName}</span>
                  </label>
                );
              })}
              {modules.length === 0 && (
                <p className="text-xs text-gray-400 italic">Brak zarejestrowanych modułów DLC.</p>
              )}
            </div>
          </div>
          </Fragment>
        );
      })}
    </div>
  );
}

// ── Companies section ─────────────────────────────────────────────────────────

const EMPTY_FORM: CompanyFormData = {
  short_code: "", name: "", is_active: true, sort_order: 0,
  street: "", postal_code: "", city: "", phone: "", nip: "",
};

/** Adres oddziału spółki — drukowany na metryce formatki (#oddzial_*#). Wspólny
 *  blok pól dla formularzy dodawania i edycji (mobile + desktop). */
function CompanyAddressFields({
  form, onChange,
}: {
  form: CompanyFormData;
  onChange: (patch: Partial<CompanyFormData>) => void;
}) {
  const cls =
    "w-full border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]";
  const rows: [string, keyof CompanyFormData][] = [
    ["Ulica i numer", "street"],
    ["Kod pocztowy", "postal_code"],
    ["Miejscowość", "city"],
    ["Telefon", "phone"],
    ["NIP", "nip"],
  ];
  return (
    <div className="flex flex-col gap-2 rounded-lg border border-dashed border-gray-300 dark:border-gray-600 p-3">
      <span className="text-[10px] font-semibold uppercase tracking-widest text-[var(--color-primary)]">
        Adres oddziału (na metryce formatki)
      </span>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {rows.map(([label, key]) => (
          <label key={key} className="flex flex-col gap-1">
            <span className="text-[10px] font-semibold uppercase tracking-widest text-gray-500 dark:text-gray-400">
              {label}
            </span>
            <input
              value={String(form[key] ?? "")}
              onChange={(e) => onChange({ [key]: e.target.value })}
              className={cls}
            />
          </label>
        ))}
      </div>
    </div>
  );
}

/** Logo firmy: podgląd + upload PNG + usunięcie. Logo trafia na formatki
 *  (plany sytuacyjne) przedsięwzięć realizowanych przez tę spółkę. */
function CompanyLogoControl({ company }: { company: Company }) {
  const { mutate: uploadLogo, isPending: uploading } = useUploadCompanyLogo();
  const { mutate: deleteLogo } = useDeleteCompanyLogo();
  const ceoConfirm = useCEOAction();
  const inputId = `logo-input-${company.id}`;

  function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ""; // pozwól wybrać ten sam plik ponownie
    if (!file) return;
    if (file.type !== "image/png") {
      window.alert("Logo musi być plikiem PNG.");
      return;
    }
    uploadLogo({ id: company.id, file });
  }

  return (
    <div className="flex items-center gap-2">
      {company.logo ? (
        <img
          src={company.logo.url}
          alt={`Logo ${company.short_code}`}
          className="h-8 w-auto max-w-[96px] object-contain rounded bg-white border border-gray-200 dark:border-gray-600 p-0.5"
        />
      ) : (
        <span className="text-xs text-gray-300 dark:text-gray-600 italic">brak</span>
      )}
      <input id={inputId} type="file" accept="image/png" className="hidden" onChange={onPick} />
      <label
        htmlFor={inputId}
        title={company.logo ? "Zmień logo (PNG)" : "Dodaj logo (PNG)"}
        className={`p-1.5 rounded cursor-pointer text-[var(--color-primary)] hover:bg-[var(--color-primary-dim)] ${uploading ? "opacity-50 pointer-events-none" : ""}`}
      >
        <ImagePlus size={16} />
      </label>
      {company.logo && (
        <button
          onClick={() => ceoConfirm(() => deleteLogo(company.id), `Usunięcie logo spółki ${company.short_code}`)}
          className="p-1.5 rounded hover:bg-red-50 dark:hover:bg-red-900/20 text-red-500"
          title="Usuń logo"
        >
          <Trash2 size={16} />
        </button>
      )}
    </div>
  );
}

function CompaniesSection() {
  const { data: companies = [], isLoading } = useCompanies();
  const { mutate: createCompany, isPending: creating } = useCreateCompany();
  const { mutate: updateCompany, isPending: updating } = useUpdateCompany();
  const { mutate: deleteCompany } = useDeleteCompany();

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<CompanyFormData>(EMPTY_FORM);
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState<CompanyFormData>(EMPTY_FORM);
  const ceoConfirm = useCEOAction();

  function startEdit(c: Company) {
    setEditingId(c.id);
    setEditForm({
      short_code: c.short_code, name: c.name, is_active: c.is_active, sort_order: c.sort_order,
      street: c.street ?? "", postal_code: c.postal_code ?? "", city: c.city ?? "",
      phone: c.phone ?? "", nip: c.nip ?? "",
    });
  }

  function cancelEdit() { setEditingId(null); }

  function saveEdit() {
    if (!editingId) return;
    ceoConfirm(
      () => updateCompany({ id: editingId!, data: editForm }, { onSuccess: () => setEditingId(null) }),
      `Edycja spółki ${editForm.short_code}`,
    );
  }

  function handleAdd() {
    if (!addForm.short_code.trim()) return;
    ceoConfirm(
      () => createCompany(addForm, { onSuccess: () => { setAddOpen(false); setAddForm(EMPTY_FORM); } }),
      `Dodanie nowej spółki ${addForm.short_code}`,
    );
  }

  function handleDelete(c: Company) {
    ceoConfirm(
      () => {
        if (!window.confirm(`Usunąć spółkę "${c.short_code}"? Tej operacji nie można cofnąć.`)) return;
        deleteCompany(c.id);
      },
      `Usunięcie spółki ${c.short_code}`,
    );
  }

  if (isLoading) return <div className="text-gray-400 text-sm py-8 text-center">Ładowanie...</div>;

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-gray-500 dark:text-gray-400">
        Zarządzaj spółkami wchodzącymi w skład GK ELEKTIM. Spółki są używane w panelu finansowym grupy.
      </p>

      {/* Mobile: card layout */}
      <div className="flex flex-col gap-2 md:hidden">
        {companies.map((c) => {
          const isEditing = editingId === c.id;
          if (isEditing) {
            return (
              <div
                key={c.id}
                className="bg-white dark:bg-gray-800 rounded-xl border-2 border-[var(--color-primary)] p-4 flex flex-col gap-3 shadow-sm"
              >
                <div className="flex flex-col gap-1">
                  <label className="text-[10px] font-semibold uppercase tracking-widest text-gray-500 dark:text-gray-400">Kod</label>
                  <input
                    value={editForm.short_code}
                    onChange={(e) => setEditForm((f) => ({ ...f, short_code: e.target.value }))}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm font-mono font-semibold bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-[10px] font-semibold uppercase tracking-widest text-gray-500 dark:text-gray-400">Nazwa</label>
                  <input
                    value={editForm.name}
                    onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
                  />
                </div>
                <div className="flex items-end gap-3">
                  <div className="flex flex-col gap-1 flex-1">
                    <label className="text-[10px] font-semibold uppercase tracking-widest text-gray-500 dark:text-gray-400">Kolejność</label>
                    <input
                      type="number"
                      value={editForm.sort_order}
                      onChange={(e) => setEditForm((f) => ({ ...f, sort_order: parseInt(e.target.value) || 0 }))}
                      className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
                    />
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer select-none py-2">
                    <input
                      type="checkbox"
                      checked={editForm.is_active}
                      onChange={(e) => setEditForm((f) => ({ ...f, is_active: e.target.checked }))}
                      className="w-5 h-5 rounded accent-[var(--color-primary)]"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">Aktywna</span>
                  </label>
                </div>
                <CompanyAddressFields form={editForm} onChange={(p) => setEditForm((f) => ({ ...f, ...p }))} />
                <div className="flex items-center gap-2 pt-1">
                  <button
                    onClick={saveEdit}
                    disabled={updating}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-green-600 hover:bg-green-700 text-white text-sm font-medium disabled:opacity-50"
                  >
                    <Check size={16} />
                    Zapisz
                  </button>
                  <button
                    onClick={cancelEdit}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    <X size={16} />
                    Anuluj
                  </button>
                </div>
              </div>
            );
          }

          return (
            <div
              key={c.id}
              className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 flex items-start justify-between gap-3 shadow-sm"
            >
              <div className="flex flex-col min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono font-bold text-base text-gray-800 dark:text-gray-100">{c.short_code}</span>
                  <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${c.is_active ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400" : "bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400"}`}>
                    {c.is_active ? "Aktywna" : "Nieaktywna"}
                  </span>
                </div>
                {c.name ? (
                  <span className="text-sm text-gray-600 dark:text-gray-300 mt-1 break-words">{c.name}</span>
                ) : (
                  <span className="text-sm text-gray-300 dark:text-gray-600 italic mt-1">brak pełnej nazwy</span>
                )}
                <span className="text-xs text-gray-400 mt-1.5">Kolejność: {c.sort_order}</span>
                <div className="mt-2 flex items-center gap-2">
                  <span className="text-[10px] font-semibold uppercase tracking-widest text-gray-400">Logo</span>
                  <CompanyLogoControl company={c} />
                </div>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <button
                  onClick={() => startEdit(c)}
                  className="p-2 rounded-lg hover:bg-[var(--color-primary-dim)] text-[var(--color-primary)]"
                  aria-label="Edytuj"
                >
                  <Pencil size={16} />
                </button>
                <button
                  onClick={() => handleDelete(c)}
                  className="p-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 text-red-500"
                  aria-label="Usuń"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          );
        })}

        {addOpen && (
          <div className="bg-[var(--color-primary-dim)]/40 dark:bg-[var(--color-primary-hover)] rounded-xl border-2 border-[var(--color-primary)] p-4 flex flex-col gap-3 shadow-sm">
            <div className="flex flex-col gap-1">
              <label className="text-[10px] font-semibold uppercase tracking-widest text-gray-500 dark:text-gray-400">Kod</label>
              <input
                autoFocus
                value={addForm.short_code}
                onChange={(e) => setAddForm((f) => ({ ...f, short_code: e.target.value }))}
                placeholder="np. ELEKTIM"
                className="w-full border border-[var(--color-primary)] rounded-lg px-3 py-2 text-sm font-mono font-semibold bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-[10px] font-semibold uppercase tracking-widest text-gray-500 dark:text-gray-400">Nazwa</label>
              <input
                value={addForm.name}
                onChange={(e) => setAddForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="Pełna nazwa (opcjonalnie)"
                className="w-full border border-[var(--color-primary)] rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
                onKeyDown={(e) => e.key === "Enter" && handleAdd()}
              />
            </div>
            <div className="flex items-end gap-3">
              <div className="flex flex-col gap-1 flex-1">
                <label className="text-[10px] font-semibold uppercase tracking-widest text-gray-500 dark:text-gray-400">Kolejność</label>
                <input
                  type="number"
                  value={addForm.sort_order}
                  onChange={(e) => setAddForm((f) => ({ ...f, sort_order: parseInt(e.target.value) || 0 }))}
                  className="w-full border border-[var(--color-primary)] rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
                />
              </div>
              <label className="flex items-center gap-2 cursor-pointer select-none py-2">
                <input
                  type="checkbox"
                  checked={addForm.is_active}
                  onChange={(e) => setAddForm((f) => ({ ...f, is_active: e.target.checked }))}
                  className="w-5 h-5 rounded accent-[var(--color-primary)]"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">Aktywna</span>
              </label>
            </div>
            <CompanyAddressFields form={addForm} onChange={(p) => setAddForm((f) => ({ ...f, ...p }))} />
            <div className="flex items-center gap-2 pt-1">
              <button
                onClick={handleAdd}
                disabled={creating || !addForm.short_code.trim()}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-green-600 hover:bg-green-700 text-white text-sm font-medium disabled:opacity-50"
              >
                <Check size={16} />
                Dodaj
              </button>
              <button
                onClick={() => { setAddOpen(false); setAddForm(EMPTY_FORM); }}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 text-sm font-medium hover:bg-white/40 dark:hover:bg-gray-700"
              >
                <X size={16} />
                Anuluj
              </button>
            </div>
          </div>
        )}

        {companies.length === 0 && !addOpen && (
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-dashed border-gray-300 dark:border-gray-700 px-4 py-8 text-center text-sm text-gray-400">
            Brak spółek. Dodaj pierwszą.
          </div>
        )}
      </div>

      {/* Desktop: table layout */}
      <div className="hidden md:block bg-white dark:bg-gray-800 overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60">
              <th className="text-left px-4 py-2.5 w-36 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Kod</th>
              <th className="text-left px-4 py-2.5 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Nazwa</th>
              <th className="text-center px-4 py-2.5 w-24 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Kolejność</th>
              <th className="text-center px-4 py-2.5 w-24 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Aktywna</th>
              <th className="text-left px-4 py-2.5 w-40 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Logo</th>
              <th className="px-4 py-2.5 w-24 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
            {companies.map((c) => {
              const isEditing = editingId === c.id;
              return (
                <Fragment key={c.id}>
                <tr className="bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors">
                  <td className="px-4 py-2.5">
                    {isEditing ? (
                      <input
                        value={editForm.short_code}
                        onChange={(e) => setEditForm((f) => ({ ...f, short_code: e.target.value }))}
                        className="w-full border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
                      />
                    ) : (
                      <span className="font-mono font-semibold text-gray-800 dark:text-gray-200">{c.short_code}</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5">
                    {isEditing ? (
                      <input
                        value={editForm.name}
                        onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
                        className="w-full border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
                      />
                    ) : (
                      <span className="text-gray-700 dark:text-gray-300">{c.name || <span className="text-gray-300 dark:text-gray-600 italic">—</span>}</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-center">
                    {isEditing ? (
                      <input
                        type="number"
                        value={editForm.sort_order}
                        onChange={(e) => setEditForm((f) => ({ ...f, sort_order: parseInt(e.target.value) || 0 }))}
                        className="w-16 border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm text-center bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
                      />
                    ) : (
                      <span className="text-gray-500 dark:text-gray-400">{c.sort_order}</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-center">
                    {isEditing ? (
                      <input
                        type="checkbox"
                        checked={editForm.is_active}
                        onChange={(e) => setEditForm((f) => ({ ...f, is_active: e.target.checked }))}
                        className="w-4 h-4 rounded accent-[var(--color-primary)]"
                      />
                    ) : (
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${c.is_active ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400" : "bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400"}`}>
                        {c.is_active ? "Tak" : "Nie"}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2.5">
                    <CompanyLogoControl company={c} />
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center justify-end gap-1">
                      {isEditing ? (
                        <>
                          <button onClick={saveEdit} disabled={updating} className="p-1.5 rounded hover:bg-green-50 dark:hover:bg-green-900/20 text-green-600" title="Zapisz">
                            <Check size={15} />
                          </button>
                          <button onClick={cancelEdit} className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500" title="Anuluj">
                            <X size={15} />
                          </button>
                        </>
                      ) : (
                        <>
                          <button onClick={() => startEdit(c)} className="p-1.5 rounded hover:bg-[var(--color-primary-dim)] dark:hover:bg-[var(--color-primary-dim)] text-[var(--color-primary)]" title="Edytuj">
                            <Pencil size={14} />
                          </button>
                          <button onClick={() => handleDelete(c)} className="p-1.5 rounded hover:bg-red-50 dark:hover:bg-red-900/20 text-red-500" title="Usuń">
                            <Trash2 size={14} />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
                {isEditing && (
                  <tr className="bg-white dark:bg-gray-800">
                    <td colSpan={6} className="px-4 pb-3">
                      <CompanyAddressFields form={editForm} onChange={(p) => setEditForm((f) => ({ ...f, ...p }))} />
                    </td>
                  </tr>
                )}
                </Fragment>
              );
            })}

            {/* Add row */}
            {addOpen && (
              <>
              <tr className="bg-[var(--color-primary-dim)]/40 dark:bg-[var(--color-primary-hover)]">
                <td className="px-4 py-2.5">
                  <input
                    autoFocus
                    value={addForm.short_code}
                    onChange={(e) => setAddForm((f) => ({ ...f, short_code: e.target.value }))}
                    placeholder="np. ELEKTIM"
                    className="w-full border border-[var(--color-primary)] dark:border-[var(--color-primary)] rounded px-2 py-1 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
                  />
                </td>
                <td className="px-4 py-2.5">
                  <input
                    value={addForm.name}
                    onChange={(e) => setAddForm((f) => ({ ...f, name: e.target.value }))}
                    placeholder="Pełna nazwa (opcjonalnie)"
                    className="w-full border border-[var(--color-primary)] dark:border-[var(--color-primary)] rounded px-2 py-1 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
                    onKeyDown={(e) => e.key === "Enter" && handleAdd()}
                  />
                </td>
                <td className="px-4 py-2.5 text-center">
                  <input
                    type="number"
                    value={addForm.sort_order}
                    onChange={(e) => setAddForm((f) => ({ ...f, sort_order: parseInt(e.target.value) || 0 }))}
                    className="w-16 border border-[var(--color-primary)] dark:border-[var(--color-primary)] rounded px-2 py-1 text-sm text-center bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
                  />
                </td>
                <td className="px-4 py-2.5 text-center">
                  <input
                    type="checkbox"
                    checked={addForm.is_active}
                    onChange={(e) => setAddForm((f) => ({ ...f, is_active: e.target.checked }))}
                    className="w-4 h-4 rounded accent-[var(--color-primary)]"
                  />
                </td>
                <td className="px-4 py-2.5">
                  <span className="text-xs text-gray-400 italic">po utworzeniu</span>
                </td>
                <td className="px-4 py-2.5">
                  <div className="flex items-center justify-end gap-1">
                    <button onClick={handleAdd} disabled={creating || !addForm.short_code.trim()} className="p-1.5 rounded hover:bg-green-50 dark:hover:bg-green-900/20 text-green-600 disabled:opacity-40" title="Dodaj">
                      <Check size={15} />
                    </button>
                    <button onClick={() => { setAddOpen(false); setAddForm(EMPTY_FORM); }} className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500" title="Anuluj">
                      <X size={15} />
                    </button>
                  </div>
                </td>
              </tr>
              <tr className="bg-[var(--color-primary-dim)]/40 dark:bg-[var(--color-primary-hover)]">
                <td colSpan={6} className="px-4 pb-3">
                  <CompanyAddressFields form={addForm} onChange={(p) => setAddForm((f) => ({ ...f, ...p }))} />
                </td>
              </tr>
              </>
            )}

            {companies.length === 0 && !addOpen && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-sm text-gray-400">Brak spółek. Dodaj pierwszą.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {!addOpen && (
        <button
          onClick={() => setAddOpen(true)}
          className="w-full md:self-start md:w-auto flex items-center justify-center gap-2 px-4 py-2.5 md:py-2 bg-[var(--color-primary)] text-white text-sm font-medium rounded-lg hover:bg-[var(--color-primary-hover)]"
        >
          <Plus size={15} />
          Dodaj spółkę
        </button>
      )}
    </div>
  );
}

// ── Roles section ─────────────────────────────────────────────────────────────

const ROLE_OPTIONS: { value: string; label: string }[] = [
  { value: "director", label: "Dyrektor" },
  { value: "coordinator", label: "Koordynator" },
  { value: "accountant", label: "Księgowy" },
  { value: "employee", label: "Pracownik" },
  { value: "hr", label: "Kadrowa" },
  { value: "contractor", label: "Podwykonawca" },
];

const ROLE_RANK: Record<string, number> = {
  contractor: 0,
  employee: 1,
  coordinator: 2,
  hr: 3,
  accountant: 3,
  director: 4,
};

function isRolePromotion(from: string, to: string): boolean {
  return (ROLE_RANK[to] ?? 0) > (ROLE_RANK[from] ?? 0);
}

type FlagDef = {
  key: keyof UserRolePatch;
  label: string;
  title?: string;
  accent?: string;
  directorOnly?: boolean;
  hideForRoles?: string[];
  hideForCEO?: boolean;
};

type FlagGroup = {
  group: string;
  icon: React.ReactNode;
  /** Widoczna wyłącznie dla CEO (nie dla osoby zarządzającej samymi przetargami). */
  ceoOnly?: boolean;
  /** Widoczna wyłącznie dla CEO, który jest RÓWNIEŻ developerem (jak `CEODevGuard`). */
  ceoDevOnly?: boolean;
  /** Doklej selektor stawki VAT (tylko pracownicy B2B). */
  withVatRate?: boolean;
  /** Doklej wybór oddziałów widocznych w Kontaktach (podwykonawca z dostępem). */
  withContactsScope?: boolean;
  flags: FlagDef[];
};

const FLAG_GROUPS: FlagGroup[] = [
  {
    group: "Status",
    icon: <Check size={13} />,
    flags: [
      { key: "is_active", label: "Aktywny", title: "Może logować się do systemu", accent: "accent-green-600" },
    ],
  },
  {
    group: "Zarząd",
    icon: <Shield size={13} />,
    ceoOnly: true,
    flags: [
      { key: "is_ceo", label: "CEO", title: "Dostęp do panelu CEO" },
      { key: "is_finance_panel", label: "Panel finansowy GK", title: "Dostęp do panelu finansowego GK" },
    ],
  },
  {
    group: "Przetargi ZE",
    icon: <Gavel size={13} />,
    flags: [
      { key: "can_view_tenders", label: "Widok przetargów", title: "Może przeglądać sekcję Przetargi" },
      { key: "can_calculate_pricing", label: "Proponowanie wycen", title: "Może proponować wyceny przetargowe" },
      { key: "can_approve_pricing", label: "Akceptacja wycen", title: "Może akceptować propozycje wycen" },
      { key: "can_issue_offers", label: "Wysyłka ofert", title: "Może oznaczać oferty jako wysłane" },
      { key: "can_view_pricing_catalog", label: "Katalogi wycen", title: "Dostęp do katalogu wycen (odczyt + edycja)" },
      { key: "has_ze_tender_analytics_access", label: "Analityka ZE", title: "Dostęp do Analityki przetargów ZE (wygrane/przegrane + wnioski AI). CEO i dyrektorzy techniczni mają ten dostęp zawsze; pozostałym nadaje wyłącznie CEO." },
      { key: "has_ze_advisor_access", label: "Doradca AI (ZE)", title: "Dostęp do Doradcy AI w projektach ZE (zakładka Doradca AI + sugestie dzienne). CEO i dyrektorzy mają ten dostęp zawsze; pozostali potrzebują dodatkowo dostępu do modułu DLC_01 (zakładka Moduły DLC)." },
    ],
  },
  {
    group: "Wyceny i obliczenia",
    icon: <Calculator size={13} />,
    flags: [
      { key: "can_use_estimating", label: "Wyceny przedmiarowe", title: "Dostęp do modułu wycen przedmiarowych / kosztorysów budowy (kosztorys nakładczy → wartość przetargowa). CEO i dyrektorzy techniczni mają ten dostęp zawsze." },
      { key: "has_tech_calc_norms_access", label: "Katalog norm (Obliczenia tech.)", title: "Dostęp do katalogu norm w module DLC_10 — Obliczenia techniczne: podgląd i edycja parametrów przewodów i słupów. Bez tego uprawnienia katalog jest niewidoczny; same obliczenia wymagają tylko dostępu do modułu DLC_10. CEO i developerzy mają ten dostęp zawsze; pozostałym nadaje wyłącznie CEO.", hideForCEO: true },
    ],
  },
  {
    group: "Postępowania ofertowe (RFQ)",
    icon: <Handshake size={13} />,
    flags: [
      { key: "has_rfq_view_access", label: "Podgląd postępowań", title: "Pozwala wejść do sekcji postępowań ofertowych (RFQ) i widzieć WSZYSTKIE postępowania, bez ich prowadzenia. Poziom najniższy. Dyrektor techniczny, CEO i prowadzący postępowania mają go zawsze; pozostałym nadaje CEO. Bez tego pracownik widzi wyłącznie postępowania, w których sam jest Kupującym.", hideForCEO: true },
      { key: "has_rfq_manage_access", label: "Prowadzenie postępowań", title: "Może zakładać i prowadzić postępowania ofertowe dla podwykonawców (RFQ). Dyrektor techniczny i CEO mają to uprawnienie zawsze; pozostałym nadaje CEO.", hideForCEO: true },
      { key: "has_rfq_sealed_access", label: "Oferty przed otwarciem", title: "Pozwala zobaczyć kwoty ofert PRZED terminem otwarcia postępowania. Celowo węższe niż prowadzenie postępowań: kto zna kwoty przed terminem, może podpowiedzieć oferentowi, ile ma dać. Domyślnie nie ma go nikt poza CEO.", hideForCEO: true },
    ],
  },
  {
    group: "Podwykonawcy",
    icon: <Truck size={13} />,
    flags: [
      { key: "can_manage_contractors", label: "Podwykonawcy i umowy ramowe", title: "Może zakładać i edytować podwykonawców oraz umowy ramowe (pozycje realizacyjne, kary umowne, polisy OC, konta podwykonawcy) i ustawiać „Zakres współpracy” podwykonawcy — kategorie prac oraz obszar działania (województwa i powiaty) — a także redagować słownik RFQ „Kategorie i zakresy” (zakładać, zmieniać i wycofywać kategorie prac i ich pozycje). Wniosków firm o rozszerzenie zakresu to uprawnienie nie obejmuje — rozpatruje je dyrektor techniczny. CEO i dyrektorzy techniczni mają to uprawnienie zawsze; pozostałym nadaje wyłącznie CEO." },
      { key: "can_manage_agreement_groups", label: "Grupy umów ramowych", title: "Może zakładać grupy umów ramowych (np. „budowlanka kujawsko-pomorskie”, „geodeci kujawsko-pomorskie”) oraz przypisywać do nich i odpinać od nich umowy. Grupy sterują tym, jak w wycenie kosztorysu rozwija się lista podwykonawców. CEO i dyrektorzy techniczni mają to uprawnienie zawsze; pozostałym nadaje wyłącznie CEO." },
      { key: "can_confirm_led_titles", label: "Potwierdzanie tytułów od firm (ZE)", title: "Może potwierdzać i odrzucać zgłoszenia pozyskania tytułu prawnego od firm prowadzących projekty ZE — po potwierdzeniu tytuł właściciela staje się „pozyskany” z dokumentami firmy. Kto ma to uprawnienie, widzi też plakietkę „N tyt.” przy Projektach ZE i filtr „Tytuły prawne do potwierdzenia” w portfelu. CEO i dyrektorzy techniczni mają je zawsze; pozostałym nadaje wyłącznie CEO. Wymaga dostępu do modułu DLC_01 (zakładka Moduły DLC).", hideForCEO: true },
      { key: "has_lead_access", label: "Pozyskiwanie podwykonawców", title: "Dostęp do modułu Pozyskiwanie: baza leadów, osoby kontaktowe, historia kontaktów, zadania i lista „nie kontaktować”. Kto ma to uprawnienie, widzi wszystkie leady — nie ma prywatnych lejków. CEO ma je zawsze; pozostałym nadaje wyłącznie CEO.", hideForCEO: true },
    ],
  },
  {
    group: "Przedsięwzięcia i zamówienia",
    icon: <Boxes size={13} />,
    flags: [
      { key: "can_approve_orders", label: "Zamawiający", title: "Może zatwierdzać zapotrzebowania (tworzy zamówienia dla podwykonawców). Od 25.09.2026 dotyczy także koordynatorów — sama rola już nie nadaje tego prawa. CEO i dyrektorzy techniczni mają je zawsze." },
      { key: "can_create_projects", label: "Zakładanie przedsięwzięć", title: "Może zakładać nowe przedsięwzięcia i automatycznie dostaje widoczność kwot w przedsięwzięciu, które sam założył (koordynator zostaje też jego koordynatorem). Nie daje wglądu w finanse pozostałych przedsięwzięć ani prawa usuwania. CEO i dyrektorzy techniczni mają to uprawnienie zawsze." },
      { key: "can_request_fees_any_project", label: "Opłaty: wszystkie przedsięwzięcia", title: "Może złożyć prośbę o opłatę urzędową na dowolnym przedsięwzięciu, także takim, którego nie koordynuje. CEO i dyrektorzy techniczni mają to uprawnienie zawsze; pozostałym nadaje wyłącznie CEO." },
      { key: "has_small_account_access", label: "Konto techniczne PKO", title: "Może wykonywać płatności opłat urzędowych" },
    ],
  },
  {
    group: "Dokumenty i korespondencja",
    icon: <FileText size={13} />,
    flags: [
      { key: "can_submit_paper_docs_for_signing", label: "Podpisy — obieg papierowy", title: "Może tworzyć klastry papierowe i dodawać dokumenty do obiegu fizycznego" },
      { key: "can_manage_doc_clusters", label: "Klastry dokumentacji", title: "Może tworzyć klastry dokumentacji projektowej (domyślnie: dyrektorzy techniczni i CEO)" },
      { key: "can_view_correspondence_register", label: "Firmowy obieg dokumentów", title: "Dostęp do firmowego dziennika korespondencji (DLC_02) — rejestr nadawczo-odbiorczy, rejestracja wpływu i dekretacja" },
      { key: "has_eop_mail_access", label: "Skrzynka EOP (poczta ENERGA)", title: "Dostęp do korespondencji ze skrzynki eop@elektim.pl w module DLC_01. Koordynatorzy przedsięwzięć ENERGA mają go z automatu — ta flaga jest dla osób spoza tego grona. Kto ma to uprawnienie, widzi CAŁĄ skrzynkę, także wątki jeszcze nieprzypisane do przedsięwzięcia; inaczej nie dałoby się ich rozpoznać. Podwykonawca nie dostaje go nigdy.", hideForCEO: true },
    ],
  },
  {
    group: "Ludzie i czas pracy",
    icon: <CalendarDays size={13} />,
    flags: [
      // „Rozkład pracy: samodzielnie" (can_self_allocate) — funkcja WYCOFANA
      // 2026-09-03 (zob. features/finances/selfAllocationFeature.ts). Flaga
      // zostaje w modelu dla danych historycznych, ale nikomu się jej nie nadaje.
      { key: "can_view_inactive_in_schedule", label: "Harmonogram: nieaktywni", title: "Może wyświetlić w harmonogramie pracy nieaktywnych (zwolnionych) pracowników i przeglądać historię ich harmonogramu. CEO ma to uprawnienie zawsze; pozostałym nadaje wyłącznie CEO." },
      { key: "can_view_all_timesheets_and_mileage", label: "Karty pracy / km: wszystkie działy", title: "Dyrektor może przeglądać karty pracy i kilometrówki ze wszystkich oddziałów, nie tylko własnego. Nadaje wyłącznie CEO.", directorOnly: true },
      { key: "is_cross_branch_visible", label: "Widoczny we wszystkich oddziałach", title: "Pracownik pojawia się na listach wyboru osób (np. przypisanie do zadania w Friday) niezależnie od oddziału wybierającego. Bez tej flagi jest widoczny tylko dla osób z własnego oddziału. Nadaje wyłącznie CEO." },
      { key: "has_contacts_access", label: "Dostęp do kontaktów", title: "Może przeglądać listę wszystkich pracowników z danymi kontaktowymi (imię, telefon, e-mail). CEO, dyrektorzy i koordynatorzy mają ten dostęp zawsze." },
      { key: "has_multisport_access", label: "Multisport", title: "Dostęp do widoku Multisport (karty pracowników). Kadrowa, Księgowa i CEO mają ten dostęp zawsze.", hideForRoles: ["hr", "accountant"], hideForCEO: true },
    ],
  },
  {
    group: "Zatrudnienie i rozliczenie",
    icon: <Coins size={13} />,
    withVatRate: true,
    flags: [
      { key: "is_uz", label: "UZ (umowa zlecenie)", title: "Umowa zlecenie" },
      { key: "is_vat_payer", label: "VATowiec (B2B)", title: "Pracownik B2B jest płatnikiem VAT — używane przy rozliczeniu faktur B2B. Ustawia wyłącznie CEO." },
      { key: "is_non_project", label: "Nieprojektowy (struktura)", title: "Pracownik strukturalny (kadry/księgowość/dokumenty) — koszt płac nie tworzy wolnych mocy, rozkłada się proporcjonalnie na grupy. Ustawia wyłącznie CEO." },
    ],
  },
  {
    group: "Narzędzia",
    icon: <Puzzle size={13} />,
    flags: [
      { key: "has_browser_extensions_access", label: "Wtyczki przeglądarkowe", title: "Dostęp do zakładki Wtyczki (wtyczki przeglądarkowe): stanowiska, paczka instalacyjna, dziennik pobrań i zdalne ustawienia. Developerzy mają ten dostęp zawsze." },
    ],
  },
  {
    // Bramka CEO+developer, jak przy Audycie ARON (`CEODevGuard`): te przełączniki
    // poszerzają to, co asystent może ZROBIĆ i ZOBACZYĆ bez pytania człowieka, więc
    // nadaje je ta sama osoba, która czyta cudze rozmowy ARON-a. Wszystkie trzy flagi
    // CEO ma z definicji (`is_ceo or ...` po stronie serwera), stąd `hideForCEO`.
    //
    // UWAGA (naprawa 2026-08-30): ta grupa stała WYŁĄCZNIE w CONTRACTOR_FLAG_GROUPS,
    // więc przełączniki pokazywały się jedynie na kontach podwykonawców — a ci ARON-a
    // nie mają i mieć nie będą. Efekt: żadnemu pracownikowi ani dyrektorowi nie dało
    // się nadać YOLO/Warsztatu z panelu (działało tylko `is_ceo` w kodzie serwera).
    group: "ARON (asystent AI)",
    icon: <Zap size={13} />,
    ceoDevOnly: true,
    flags: [
      { key: "can_use_aron_autopilot", label: "Tryb samodzielny (YOLO)", title: "Pozwala prowadzić rozmowy z ARON-em w trybie YOLO: asystent zapisuje zmiany w systemie OD RAZU, bez kart do zatwierdzenia. Ta sama bramka obowiązuje w zadaniach cyklicznych (planner) — dopiero z nią da się ustawić zadanie w trybie samodzielnym. Uprawnienie zdejmuje też limit kroków w turze. CEO ma je zawsze.", hideForCEO: true },
      { key: "can_use_ai_workshop", label: "Warsztat AI + Rentgen (wyrocznia kodowa)", title: "ZAWIERA RENTGEN — wyrocznię kodową: ARON zadaje pytanie osobnemu modelowi, ten czyta kod systemu i zwraca gotową odpowiedź z cytatami „plik:linia”. ARON nie dostaje repozytorium, a wyrocznia nie ma dostępu do sekretów, ustawień, .env ani infrastruktury. Poza tym Warsztat to uruchamianie skryptów odczytowo-obliczeniowych oraz czytanie dokumentów i rysunków. Ta flaga jest ręcznym nadaniem poza modułem DLC_03 — kto ma DLC_03, ma Warsztat (i Rentgena) bez niej. CEO ma je zawsze.", hideForCEO: true },
      { key: "can_read_source_code", label: "Odczyt kodu źródłowego (awaryjnie)", title: "SUROWY podgląd plików repozytorium: ARON sam przeszukuje kod (grep_code) i wciąga całe pliki do rozmowy (read_code). W praktyce NIE jest potrzebny — Rentgen (w Warsztacie AI) odpowiada szybciej, taniej i od razu z cytatami. To awaryjna furtka: gdy ktoś potrzebuje DOSŁOWNEGO fragmentu pliku albo chce zweryfikować odpowiedź wyroczni. Domyślnie nie ma tego nikt i nie ma potrzeby nikomu tego nadawać. Osobne uprawnienie, bo wyciek kodu to własna klasa ryzyka — ani DLC_03, ani Warsztat AI go NIE otwierają. Nadaje wyłącznie CEO (serwer odrzuca zmianę od kogokolwiek innego). CEO ma je zawsze.", hideForCEO: true },
    ],
  },
];

const CONTRACTOR_FLAG_GROUPS: FlagGroup[] = [
  {
    group: "Status",
    icon: <Check size={13} />,
    flags: [
      { key: "is_active", label: "Aktywny", title: "Może logować się do systemu", accent: "accent-green-600" },
    ],
  },
  {
    group: "Dostęp",
    icon: <Puzzle size={13} />,
    flags: [
      { key: "has_friday_access", label: "Friday (zadania)", title: "Dostęp do modułu zarządzania zadaniami Friday" },
    ],
  },
  // ARON-a (YOLO / Warsztat / odczyt kodu) tu NIE MA i mieć nie będzie: decyzja
  // właściciela — „Podwykonawca nie ma i nigdy nie będzie miał ARON-a”. Przełącznik,
  // który nic nie daje, tylko wprowadza w błąd; grupa „ARON (asystent AI)” stoi
  // w FLAG_GROUPS, czyli tam, gdzie da się ją komukolwiek nadać.
  {
    // Osobna grupa z `ceoOnly`, a nie flaga dorzucona do „Dostępu": wpuszczenie kogoś
    // z zewnątrz do wewnętrznej rozmowy firmy nadaje wyłącznie CEO, więc przełącznik
    // ma się nawet nie pokazywać komuś, kto i tak dostanie odmowę z serwera.
    group: "Komunikator GG",
    icon: <Sun size={13} />,
    ceoOnly: true,
    flags: [
      { key: "has_chat_access", label: "Wstęp do GG", title: "Wpuszcza to konto podwykonawcy do komunikatora GG. Bez tego nie widzi modułu i nie da się go dodać do żadnej rozmowy. Dodany do istniejącej grupy widzi ją dopiero OD MOMENTU dołączenia — wcześniejszych wiadomości nie zobaczy. Rozmowę zawsze zaczyna pracownik GK; podwykonawca sam nie zakłada rozmów ani nie dodaje uczestników." },
    ],
  },
  {
    // Ta sama zasada co przy GG: na dyskach leżą oferty, umowy i kalkulacje, więc
    // wpuszczenie firmy z zewnątrz jest decyzją CEO, nie ustawieniem kartoteki.
    group: "Dysk TwoDrive",
    icon: <HardDrive size={13} />,
    ceoOnly: true,
    flags: [
      { key: "has_two_drive_access", label: "Wstęp na dysk", title: "Otwiera temu kontu podwykonawcy dysk TwoDrive: własne drzewo plików, wgrywanie, pobieranie i udostępnianie. Bez tego zewnętrzne konto nie widzi modułu i NIE DA SIĘ mu nic udostępnić. Świeże konto dostaje 10 GB (pracownik GK 50 GB) — limit podnosi CEO przyciskiem „Limity” na dysku. Cofnięcie flagi odcina dostęp natychmiast, razem z linkami do plików już udostępnionych; pliki zostają na dysku i wracają razem z flagą." },
    ],
  },
  {
    // Jak GG i TwoDrive: katalog telefonów i e-maili pracowników GK otwierany firmie
    // z zewnątrz, więc wstęp i zakres oddziałów ustawia wyłącznie CEO (serwer
    // odrzuca zmianę od kogokolwiek innego).
    group: "Kontakty",
    icon: <Users size={13} />,
    ceoOnly: true,
    withContactsScope: true,
    flags: [
      { key: "has_contacts_access", label: "Dostęp do kontaktów", title: "Otwiera temu kontu podwykonawcy katalog Kontakty (imię i nazwisko, telefon, e-mail pracowników GK) — ale tylko z oddziałów wybranych na liście poniżej. Bez wybranego oddziału lista kontaktów jest pusta." },
    ],
  },
];

/** Grupy widoczne dla danego użytkownika + odfiltrowane flagi (identyczne reguły jak dotychczas).
 *  Eksportowana WYŁĄCZNIE po to, żeby test regresyjny pytał o ten sam kod, który
 *  renderuje panel (grupa ARON przez pół roku wisiała w tablicy podwykonawców). */
export function visibleFlagGroups(
  u: { role: string; is_ceo?: boolean; contract_type?: string | null },
  viewerIsCEO: boolean,
  viewerIsDev: boolean,
): { group: FlagGroup; flags: FlagDef[] }[] {
  const source = u.role === "contractor" ? CONTRACTOR_FLAG_GROUPS : FLAG_GROUPS;
  return source
    .filter((g) => viewerIsCEO || !g.ceoOnly)
    .filter((g) => !g.ceoDevOnly || (viewerIsCEO && viewerIsDev))
    .map((g) => ({
      group: g,
      flags: g.flags.filter(
        ({ key, directorOnly, hideForRoles, hideForCEO }) =>
          (key !== "is_vat_payer" || u.contract_type === "b2b") &&
          (!directorOnly || u.role === "director") &&
          (!hideForRoles || !hideForRoles.includes(u.role)) &&
          !(hideForCEO && u.is_ceo),
      ),
    }))
    .filter(({ group, flags }) => flags.length > 0 || (group.withVatRate && u.contract_type === "b2b"));
}

function JobTitleInput({
  value,
  disabled,
  onSave,
  fullWidth = false,
}: {
  value: string;
  disabled: boolean;
  onSave: (val: string) => void;
  fullWidth?: boolean;
}) {
  const [draft, setDraft] = useState(value);
  const isDirty = draft !== value;
  return (
    <div className={`flex items-center gap-1 ${fullWidth ? "w-full" : ""}`}>
      <input
        type="text"
        value={draft}
        disabled={disabled}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => { if (isDirty) onSave(draft); }}
        onKeyDown={(e) => { if (e.key === "Enter") { e.currentTarget.blur(); } if (e.key === "Escape") { setDraft(value); } }}
        placeholder="brak"
        className={`${fullWidth ? "w-full" : "w-44"} rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-1 text-sm text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] disabled:opacity-50`}
      />
      {isDirty && <span className="text-xs text-[var(--color-primary)]">•</span>}
    </div>
  );
}

const VAT_RATE_OPTIONS: { value: "" | "zw" | "0" | "5" | "8" | "23"; label: string }[] = [
  { value: "", label: "VAT: brak" },
  { value: "zw", label: "VAT: Zwolniony" },
  { value: "0", label: "VAT: 0%" },
  { value: "5", label: "VAT: 5%" },
  { value: "8", label: "VAT: 8%" },
  { value: "23", label: "VAT: 23%" },
];

function VatRateSelector({
  user,
  disabled,
  patch,
  ceoConfirm,
}: {
  user: { id: string; first_name: string; last_name: string; username: string; vat_rate?: "zw" | "0" | "5" | "8" | "23" | null };
  disabled: boolean;
  patch: (id: string, data: UserRolePatch) => void;
  ceoConfirm: CEOConfirmFn;
}) {
  const current = user.vat_rate ?? "";
  return (
    <label
      title="Stawka VAT pracownika B2B (ustawia wyłącznie CEO)"
      className={`flex items-center gap-1.5 select-none ${disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`}
    >
      <select
        value={current}
        disabled={disabled}
        onChange={(e) => {
          const next = e.target.value as "" | "zw" | "0" | "5" | "8" | "23";
          const userName = `${user.first_name} ${user.last_name}`.trim() || user.username;
          const description =
            next === ""
              ? `Usunięcie stawki VAT dla ${userName}`
              : `Ustawienie stawki VAT „${next === "zw" ? "Zwolniony" : next + "%"}" dla ${userName}`;
          ceoConfirm(
            () => patch(user.id, { vat_rate: next === "" ? null : next }),
            description,
          );
        }}
        className="rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-1.5 py-0.5 text-xs text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)] disabled:opacity-50"
      >
        {VAT_RATE_OPTIONS.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </label>
  );
}

/** Wartownik pozycji „Pracownicy bez oddziału" na liście oddziałów Kontaktów. */
const NO_BRANCH = "__no_branch__";

/** Zakres Kontaktów podwykonawcy: pracowników których oddziałów widzi. Lista rozwija
 *  się w karcie (nie jako warstwa — tabela użytkowników ma `overflow-hidden`, a
 *  podwykonawcy stoją na jej końcu), a wybór trafia na serwer jednym potwierdzeniem
 *  CEO po „Zapisz", nie po każdym kliknięciu. */
export function ContactsScopeSelector({
  user,
  disabled,
  patch,
  ceoConfirm,
}: {
  user: {
    id: string;
    first_name: string;
    last_name: string;
    username: string;
    contacts_branches?: string[];
    contacts_include_no_branch?: boolean;
  };
  disabled: boolean;
  patch: (id: string, data: UserRolePatch) => void;
  ceoConfirm: CEOConfirmFn;
}) {
  const { data: branches = [] } = useBranches();
  const [open, setOpen] = useState(false);
  const saved = [
    ...(user.contacts_branches ?? []),
    ...(user.contacts_include_no_branch ? [NO_BRANCH] : []),
  ];
  const savedKey = [...saved].sort().join(",");
  // The draft remembers which saved state it was based on — once the server value
  // changes (save landed, someone else edited) it is dropped instead of lingering.
  const [draft, setDraft] = useState<{ base: string; value: string[] } | null>(null);
  const value = draft && draft.base === savedKey ? draft.value : saved;
  const dirty = [...value].sort().join(",") !== savedKey;

  const options = [
    ...branches
      .filter((b) => b.is_active || saved.includes(b.id))
      .map((b) => ({ value: b.id, label: b.is_active ? b.name : `${b.name} (nieaktywny)` })),
    { value: NO_BRANCH, label: "Pracownicy bez oddziału" },
  ];
  const selected = options.filter((o) => value.includes(o.value));

  const toggle = (v: string) =>
    setDraft({
      base: savedKey,
      value: value.includes(v) ? value.filter((x) => x !== v) : [...value, v],
    });

  function save() {
    const userName = `${user.first_name} ${user.last_name}`.trim() || user.username;
    const next = value;
    ceoConfirm(
      () =>
        patch(user.id, {
          contacts_branches: next.filter((v) => v !== NO_BRANCH),
          contacts_include_no_branch: next.includes(NO_BRANCH),
        }),
      selected.length > 0
        ? `Kontakty dla ${userName}: ${selected.map((o) => o.label).join(", ")}`
        : `Kontakty dla ${userName}: żaden oddział (lista kontaktów będzie pusta)`,
    );
    setOpen(false);
  }

  return (
    <div className="flex flex-col gap-1">
      <span className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400">
        Widoczne oddziały
      </span>
      <button
        type="button"
        disabled={disabled}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="flex items-start gap-1.5 w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-1.5 py-1 text-left text-xs focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)] disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <span className="flex-1 flex flex-wrap gap-1 min-w-0">
          {selected.length === 0 ? (
            <span className="text-amber-600 dark:text-amber-400">Nikt — wybierz oddziały</span>
          ) : (
            selected.map((o) => (
              <span
                key={o.value}
                className="max-w-full truncate rounded bg-[var(--color-primary-dim)] px-1.5 py-0.5 text-[var(--color-primary)]"
              >
                {o.label}
              </span>
            ))
          )}
        </span>
        <ChevronDown
          size={12}
          className={`mt-0.5 shrink-0 text-gray-400 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <div className="max-h-56 overflow-y-auto rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 py-1">
          {options.map((o) => (
            <label
              key={o.value}
              className={`flex items-center gap-2 px-2 py-1 text-xs cursor-pointer select-none text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/50 ${
                o.value === NO_BRANCH ? "mt-1 pt-1.5 border-t border-gray-100 dark:border-gray-700 italic" : ""
              }`}
            >
              <input
                type="checkbox"
                checked={value.includes(o.value)}
                onChange={() => toggle(o.value)}
                className="w-3.5 h-3.5 rounded accent-[var(--color-primary)] flex-shrink-0"
              />
              <span className="truncate">{o.label}</span>
            </label>
          ))}
        </div>
      )}
      {dirty && (
        <div className="flex items-center justify-end gap-1.5">
          <button
            type="button"
            onClick={() => {
              setDraft(null);
              setOpen(false);
            }}
            className="rounded px-2 py-0.5 text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            Anuluj
          </button>
          <button
            type="button"
            disabled={disabled}
            onClick={save}
            className="rounded bg-[var(--color-primary)] px-2 py-0.5 text-xs font-medium text-white hover:opacity-90 disabled:opacity-50"
          >
            Zapisz
          </button>
        </div>
      )}
    </div>
  );
}

type PendingRoleChange = {
  userId: string;
  userName: string;
  fromRole: string;
  toRole: string;
};

type ManagedUserLike = ReturnType<typeof useAllUsers>["data"] extends (infer T)[] | undefined ? T : never;

// ── Permission grid (shared: desktop drawer + mobile card) ────────────────────

/** Ile nadanych uprawnień pokazujemy w zwiniętym wierszu, zanim zwiniemy resztę do „+N". */
const CHIP_LIMIT = 6;

function flagValue(u: ManagedUserLike, key: keyof UserRolePatch): boolean {
  return Boolean((u as unknown as Record<string, unknown>)[key]);
}

/** Etykiety nadanych uprawnień — skrót pokazywany w zwiniętym wierszu. */
function grantedFlagLabels(u: ManagedUserLike, viewerIsCEO: boolean, viewerIsDev: boolean): string[] {
  return visibleFlagGroups(u, viewerIsCEO, viewerIsDev)
    .flatMap(({ flags }) => flags)
    .filter((f) => f.key !== "is_active" && flagValue(u, f.key))
    .map((f) => f.label);
}

function PermissionGrid({
  u,
  isSelf,
  isCEO,
  isDev,
  isPending,
  ceoConfirm,
  patch,
  singleColumn = false,
}: {
  u: ManagedUserLike;
  isSelf: boolean;
  isCEO: boolean;
  isDev: boolean;
  isPending: boolean;
  ceoConfirm: CEOConfirmFn;
  patch: (id: string, data: UserRolePatch) => void;
  singleColumn?: boolean;
}) {
  const userName = `${u.first_name} ${u.last_name}`.trim() || u.username;
  const flagDisabled = (isSelf && !isCEO) || isPending || (!isCEO && u.role === "director");
  const groups = visibleFlagGroups(u, isCEO, isDev);

  return (
    <div
      className={`grid gap-2 items-start ${
        singleColumn ? "grid-cols-1" : "grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4"
      }`}
    >
      {groups.map(({ group, flags }) => {
        const granted = flags.filter((f) => flagValue(u, f.key)).length;
        return (
          <div
            key={group.group}
            className="rounded-lg border border-gray-200 dark:border-gray-700/60 bg-gray-50/70 dark:bg-gray-900/30 p-2.5 flex flex-col"
          >
            <div className="flex items-center gap-1.5 pb-1.5 mb-1 border-b border-gray-200/80 dark:border-gray-700/50">
              <span className="text-gray-400 dark:text-gray-500 flex-shrink-0">{group.icon}</span>
              <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 leading-none">
                {group.group}
              </span>
              {flags.length > 0 && (
                <span
                  className={`ml-auto text-[10px] font-mono leading-none px-1.5 py-0.5 rounded-full ${
                    granted > 0
                      ? "bg-[var(--color-primary-dim)] text-[var(--color-primary)]"
                      : "text-gray-400 dark:text-gray-600"
                  }`}
                >
                  {granted}/{flags.length}
                </span>
              )}
            </div>

            <div className="flex flex-col gap-px">
              {flags.map(({ key, label, title, accent }) => {
                const val = flagValue(u, key);
                return (
                  <label
                    key={key}
                    title={title}
                    className={`flex items-start gap-2 rounded px-1 py-1 select-none transition-colors ${
                      flagDisabled
                        ? "cursor-not-allowed opacity-60"
                        : "cursor-pointer hover:bg-white dark:hover:bg-gray-800/70"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={val}
                      disabled={flagDisabled}
                      onChange={() =>
                        ceoConfirm(
                          () => {
                            if (key === "is_uz") {
                              patch(u.id, { contract_type: !val ? "uz" : "uop" });
                            } else {
                              patch(u.id, { [key]: !val } as UserRolePatch);
                            }
                          },
                          `${val ? "Cofnięcie" : "Nadanie"} uprawnienia "${label}" dla ${userName}`,
                        )
                      }
                      className={`w-3.5 h-3.5 mt-px rounded ${accent ?? "accent-[var(--color-primary)]"} disabled:cursor-not-allowed flex-shrink-0`}
                    />
                    <span
                      className={`text-xs leading-snug ${
                        val
                          ? "font-medium text-gray-800 dark:text-gray-100"
                          : "text-gray-500 dark:text-gray-400"
                      }`}
                    >
                      {label}
                    </span>
                  </label>
                );
              })}

              {group.withVatRate && u.contract_type === "b2b" && (
                <div className="pt-1.5 mt-1 border-t border-gray-200/80 dark:border-gray-700/50">
                  <VatRateSelector
                    user={u}
                    disabled={(isSelf && !isCEO) || isPending || !isCEO}
                    patch={patch}
                    ceoConfirm={ceoConfirm}
                  />
                </div>
              )}

              {group.withContactsScope && flagValue(u, "has_contacts_access") && (
                <div className="pt-1.5 mt-1 border-t border-gray-200/80 dark:border-gray-700/50">
                  <ContactsScopeSelector
                    user={u}
                    disabled={flagDisabled || !isCEO}
                    patch={patch}
                    ceoConfirm={ceoConfirm}
                  />
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Mobile user card ──────────────────────────────────────────────────────────

function UserCardMobile({
  u,
  isSelf,
  isCEO,
  isDev,
  isPending,
  branches,
  ceoConfirm,
  patch,
  handleRoleChange,
}: {
  u: ManagedUserLike;
  isSelf: boolean;
  isCEO: boolean;
  isDev: boolean;
  isPending: boolean;
  branches: Branch[];
  ceoConfirm: CEOConfirmFn;
  patch: (id: string, data: UserRolePatch) => void;
  handleRoleChange: (userId: string, userName: string, fromRole: string, toRole: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const userName = `${u.first_name} ${u.last_name}`.trim() || u.username;
  const grantedCount = grantedFlagLabels(u, isCEO, isDev).length;

  return (
    <div
      className={`rounded-xl border overflow-hidden ${
        isSelf
          ? "bg-blue-50/40 dark:bg-blue-900/10 border-blue-200 dark:border-blue-800/40"
          : "bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700"
      } ${u.is_active ? "" : "opacity-50 grayscale"}`}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-3 p-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700/40 transition-colors"
      >
        <UserAvatar
          userId={u.id}
          fullName={userName}
          profilePictureUrl={u.profile_picture_url}
          size="sm"
        />
        <div className="flex-1 min-w-0">
          <div className="font-medium text-sm text-gray-800 dark:text-gray-200 truncate">
            {userName}
            {isSelf && (
              <span className="ml-1.5 text-xs text-[var(--color-primary)] dark:text-blue-400">(Ty)</span>
            )}
          </div>
          <div className="text-xs text-gray-400 truncate">
            {u.username} · {getRoleLabel(u.role)} · {grantedCount} upr.
          </div>
        </div>
        {!u.is_active && (
          <span className="text-[10px] bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 rounded px-1.5 py-0.5 font-medium flex-shrink-0">
            nieaktywny
          </span>
        )}
        {u.is_ceo && (
          <span className="text-[10px] bg-[var(--color-primary-dim)] text-[var(--color-primary)] rounded px-1.5 py-0.5 font-medium flex-shrink-0">
            CEO
          </span>
        )}
        <ChevronDown
          size={16}
          className={`text-gray-400 flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <div className="border-t border-gray-100 dark:border-gray-700/50 px-3 py-2 flex flex-col gap-2">
          {/* Role */}
          <div>
            <label className="block text-[10px] font-semibold uppercase tracking-widest text-gray-500 dark:text-gray-400 mb-0.5">
              Rola
            </label>
            <select
              value={u.role}
              disabled={isSelf || isPending || u.role === "contractor" || (!isCEO && u.role === "director")}
              onChange={(e) => {
                const toRole = e.target.value;
                if (isCEO) {
                  ceoConfirm(
                    () => patch(u.id, { role: toRole as UserRolePatch["role"] }),
                    `Zmiana roli ${userName} → ${getRoleLabel(toRole)}`,
                  );
                } else {
                  handleRoleChange(u.id, userName, u.role, toRole);
                }
              }}
              className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {ROLE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value} disabled={!isCEO && opt.value === "director"}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {/* Job title */}
          <div>
            <label className="block text-[10px] font-semibold uppercase tracking-widest text-gray-500 dark:text-gray-400 mb-0.5">
              Stanowisko
            </label>
            <JobTitleInput
              value={u.job_title ?? ""}
              disabled={isPending}
              fullWidth
              onSave={(val) =>
                ceoConfirm(
                  () => patch(u.id, { job_title: val }),
                  `Zmiana stanowiska ${userName} → "${val}"`,
                )
              }
            />
          </div>

          {/* Branch */}
          {u.role !== "contractor" && (
            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-widest text-gray-500 dark:text-gray-400 mb-0.5">
                Oddział
              </label>
              <select
                value={(u.branch as unknown as { id: string } | null)?.id ?? ""}
                disabled={!isCEO || isPending}
                onChange={(e) => {
                  const branchId = e.target.value || null;
                  const branchName = branches.find((b) => b.id === branchId)?.name ?? "brak";
                  ceoConfirm(
                    () => patch(u.id, { branch: branchId }),
                    `Zmiana oddziału ${userName} → ${branchName}`,
                  );
                }}
                className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <option value="">— brak —</option>
                {branches.map((b) => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
              {u.role === "director" && isCEO && (
                <label
                  title="Dyrektor techniczny ma dostęp do sekcji projektowych i przetargów"
                  className="flex items-center gap-2 mt-1.5 cursor-pointer select-none"
                >
                  <input
                    type="checkbox"
                    checked={!!u.is_technical_director}
                    disabled={isPending}
                    onChange={() =>
                      ceoConfirm(
                        () => patch(u.id, { is_technical_director: !u.is_technical_director }),
                        `Zmiana statusu Dyrektora Technicznego ${userName}`,
                      )
                    }
                    className="w-4 h-4 accent-[var(--color-primary)]"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Dyrektor techniczny</span>
                </label>
              )}
            </div>
          )}

          {/* Uprawnienia */}
          <div>
            <span className="block text-[10px] font-semibold uppercase tracking-widest text-gray-500 dark:text-gray-400 mb-1">
              Uprawnienia
            </span>
            <PermissionGrid
              u={u}
              isSelf={isSelf}
              isCEO={isCEO}
              isDev={isDev}
              isPending={isPending}
              ceoConfirm={ceoConfirm}
              patch={patch}
              singleColumn
            />
          </div>
        </div>
      )}
    </div>
  );
}

function RolesSection() {
  const { data: users = [], isLoading } = useAllUsers();
  const { mutate: patchUser, isPending } = usePatchUserRole();
  const { data: branches = [] } = useBranches();
  const { user: me } = useAuthStore();
  const isCEO = !!me?.is_ceo;
  const isDev = !!me?.is_developer;
  const ceoConfirm = useCEOAction();
  const [search, setSearch] = useState("");
  const [pendingRoleChange, setPendingRoleChange] = useState<PendingRoleChange | null>(null);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [showInactive, setShowInactive] = useState(false);

  // Zablokowani (nieaktywni) to ARCHIWUM, nie codzienna praca: nadając uprawnienia
  // patrzy się na ludzi, którzy się logują. Dlatego domyślnie są UKRYCI, a po
  // odsłonięciu lądują na KOŃCU listy i wyszarzone — żeby nikt nie nadał dostępu
  // komuś, kto już nie pracuje, biorąc go za czynnego pracownika.
  const matched = users.filter((u) =>
    `${u.first_name} ${u.last_name} ${u.username}`.toLowerCase().includes(search.toLowerCase())
  );
  const activeMatched = matched.filter((u) => u.is_active);
  const inactiveMatched = matched.filter((u) => !u.is_active);
  const filtered = showInactive ? [...activeMatched, ...inactiveMatched] : activeMatched;
  // „Brak wyników" byłoby nieprawdą, gdy szukana osoba JEST — tylko zablokowana
  // i schowana. Powiedz to wprost, zamiast kazać zgadywać, czemu nie widać kolegi.
  const emptyStateText = inactiveMatched.length > 0
    ? `Nie ma czynnych osób dla „${search}" — pasuje ${inactiveMatched.length} zablokowanych. Kliknij „Pokaż zablokowanych”.`
    : `Brak wyników dla „${search}"`;

  const allExpanded = filtered.length > 0 && filtered.every((u) => expanded[u.id]);

  function toggleExpanded(id: string) {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  }

  function toggleExpandAll() {
    setExpanded(allExpanded ? {} : Object.fromEntries(filtered.map((u) => [u.id, true])));
  }

  function patch(id: string, data: UserRolePatch) {
    patchUser({ id, data });
  }

  function handleRoleChange(userId: string, userName: string, fromRole: string, toRole: string) {
    if (isRolePromotion(fromRole, toRole)) {
      setPendingRoleChange({ userId, userName, fromRole, toRole });
    } else {
      patch(userId, { role: toRole as UserRolePatch["role"] });
    }
  }

  function confirmRoleChange() {
    if (!pendingRoleChange) return;
    patchUser(
      { id: pendingRoleChange.userId, data: { role: pendingRoleChange.toRole as UserRolePatch["role"] } },
      { onSuccess: () => setPendingRoleChange(null) },
    );
  }

  if (isLoading) {
    return <PageLoader />;
  }

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-gray-500 dark:text-gray-400">
        Zarządzaj rolami i uprawnieniami wszystkich użytkowników systemu.
        Kolumna „Nadane uprawnienia" pokazuje, co dana osoba ma — rozwiń wiersz, aby nadać
        lub cofnąć uprawnienie w konkretnym obszarze.
      </p>

      <div className="flex flex-wrap items-center gap-2">
        <input
          type="search"
          placeholder="Szukaj po nazwisku, imieniu lub loginie…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full max-w-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-1.5 text-sm text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
        />
        <span className="text-xs text-gray-400">{filtered.length} os.</span>
        {inactiveMatched.length > 0 && (
          <button
            type="button"
            onClick={() => setShowInactive((v) => !v)}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-gray-300 dark:border-gray-600 text-xs font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            title="Osoby zablokowane (nieaktywne) — nie logują się do systemu. Domyślnie ukryte, żeby nie nadać im dostępu przez pomyłkę."
          >
            {showInactive ? <EyeOff size={13} /> : <Eye size={13} />}
            {showInactive ? "Ukryj zablokowanych" : `Pokaż zablokowanych (${inactiveMatched.length})`}
          </button>
        )}
        <button
          type="button"
          onClick={toggleExpandAll}
          disabled={filtered.length === 0}
          className="hidden md:flex items-center gap-1.5 ml-auto px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 text-xs font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-40"
        >
          <ChevronDown size={13} className={`transition-transform ${allExpanded ? "rotate-180" : ""}`} />
          {allExpanded ? "Zwiń wszystkie" : "Rozwiń wszystkie"}
        </button>
      </div>

      {/* Desktop table */}
      <div className="hidden md:block rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        <table className="w-full text-xs table-fixed">
          <thead>
            <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60">
              <th className="text-left px-4 py-2.5 w-[24%] font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Użytkownik</th>
              <th className="text-left px-4 py-2.5 w-[13%] font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Rola</th>
              <th className="text-left px-4 py-2.5 w-[18%] font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Stanowisko</th>
              <th className="text-left px-4 py-2.5 w-[15%] font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Oddział</th>
              <th className="text-left px-4 py-2.5 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Nadane uprawnienia</th>
            </tr>
          </thead>
          {filtered.map((u) => {
              const isSelf = u.id === me?.id;
              const isOpen = !!expanded[u.id];
              const granted = grantedFlagLabels(u, isCEO, isDev);
              const shownChips = granted.slice(0, CHIP_LIMIT);
              return (
                <tbody
                  key={u.id}
                  className={`border-b border-gray-100 dark:border-gray-700/50 last:border-b-0 ${
                    u.is_active ? "" : "opacity-50 grayscale"
                  }`}
                >
                <tr
                  className={`${isSelf ? "bg-blue-50/40 dark:bg-blue-900/10" : "bg-white dark:bg-gray-800"} ${isOpen ? "!bg-gray-50 dark:!bg-gray-700/30" : ""} hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors`}
                >
                  {/* Name */}
                  <td className="px-4 py-2.5 align-top">
                    <div className="flex items-center gap-2">
                      <UserAvatar
                        userId={u.id}
                        fullName={`${u.first_name} ${u.last_name}`.trim() || u.username}
                        profilePictureUrl={u.profile_picture_url}
                        size="sm"
                      />
                      <div className="min-w-0">
                        <div className="font-medium text-gray-800 dark:text-gray-200 leading-tight truncate">
                          {u.first_name || u.last_name
                            ? `${u.first_name} ${u.last_name}`.trim()
                            : u.username}
                          {isSelf && (
                            <span className="ml-1.5 text-xs text-[var(--color-primary)] dark:text-blue-400">(Ty)</span>
                          )}
                        </div>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          <span className="text-xs text-gray-400 truncate">{u.username}</span>
                          {u.is_ceo && (
                            <span className="text-[10px] leading-none bg-[var(--color-primary-dim)] text-[var(--color-primary)] rounded px-1 py-0.5 font-medium flex-shrink-0">
                              CEO
                            </span>
                          )}
                          {!u.is_active && (
                            <span className="text-[10px] leading-none bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 rounded px-1 py-0.5 font-medium flex-shrink-0">
                              nieaktywny
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </td>

                  {/* Role dropdown */}
                  <td className="px-4 py-2.5 align-top">
                    <select
                      value={u.role}
                      disabled={isSelf || isPending || u.role === "contractor" || (!isCEO && u.role === "director")}
                      onChange={(e) => {
                        const toRole = e.target.value;
                        const userName = `${u.first_name} ${u.last_name}`.trim() || u.username;
                        if (isCEO) {
                          ceoConfirm(
                            () => patch(u.id, { role: toRole as UserRolePatch["role"] }),
                            `Zmiana roli ${userName} → ${getRoleLabel(toRole)}`,
                          );
                        } else {
                          handleRoleChange(u.id, userName, u.role, toRole);
                        }
                      }}
                      className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {ROLE_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value} disabled={!isCEO && opt.value === "director"}>{opt.label}</option>
                      ))}
                    </select>
                  </td>

                  {/* Job title */}
                  <td className="px-4 py-2.5 align-top">
                    <JobTitleInput
                      value={u.job_title ?? ""}
                      disabled={isPending}
                      fullWidth
                      onSave={(val) => ceoConfirm(
                        () => patch(u.id, { job_title: val }),
                        `Zmiana stanowiska ${`${u.first_name} ${u.last_name}`.trim() || u.username} → "${val}"`,
                      )}
                    />
                  </td>

                  {/* Branch + is_technical_director */}
                  <td className="px-4 py-2.5 align-top">
                    {u.role !== "contractor" && (
                      <div className="flex flex-col gap-1.5">
                        <select
                          value={(u.branch as unknown as { id: string } | null)?.id ?? ""}
                          disabled={!isCEO || isPending}
                          onChange={(e) => {
                            const branchId = e.target.value || null;
                            const branchName = branches.find((b) => b.id === branchId)?.name ?? "brak";
                            ceoConfirm(
                              () => patch(u.id, { branch: branchId }),
                              `Zmiana oddziału ${`${u.first_name} ${u.last_name}`.trim() || u.username} → ${branchName}`,
                            );
                          }}
                          className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)] disabled:opacity-50 disabled:cursor-not-allowed w-full"
                        >
                          <option value="">— brak —</option>
                          {branches.map((b) => (
                            <option key={b.id} value={b.id}>{b.name}</option>
                          ))}
                        </select>
                        {u.role === "director" && isCEO && (
                          <label title="Dyrektor techniczny ma dostęp do sekcji projektowych i przetargów" className="flex items-center gap-1.5 cursor-pointer select-none">
                            <input
                              type="checkbox"
                              checked={!!u.is_technical_director}
                              disabled={isPending}
                              onChange={() => ceoConfirm(
                                () => patch(u.id, { is_technical_director: !u.is_technical_director }),
                                `Zmiana statusu Dyrektora Technicznego ${`${u.first_name} ${u.last_name}`.trim() || u.username}`,
                              )}
                              className="w-3.5 h-3.5 accent-[var(--color-primary)]"
                            />
                            <span className="text-xs text-gray-600 dark:text-gray-400">Techniczny</span>
                          </label>
                        )}
                      </div>
                    )}
                  </td>

                  {/* Granted permissions summary + drawer toggle */}
                  <td className="px-4 py-2.5 align-top">
                    <button
                      type="button"
                      onClick={() => toggleExpanded(u.id)}
                      className="w-full flex items-start gap-3 text-left"
                      title={isOpen ? "Zwiń listę uprawnień" : "Rozwiń, aby nadać lub cofnąć uprawnienia"}
                    >
                      <span className="flex-1 flex flex-wrap gap-1 min-w-0">
                        {granted.length === 0 ? (
                          <span className="text-xs text-gray-400 italic">brak dodatkowych uprawnień</span>
                        ) : (
                          <>
                            {shownChips.map((label) => (
                              <span
                                key={label}
                                className="text-[11px] leading-none px-1.5 py-1 rounded bg-[var(--color-primary-dim)] text-[var(--color-primary)] font-medium"
                              >
                                {label}
                              </span>
                            ))}
                            {granted.length > shownChips.length && (
                              <span className="text-[11px] leading-none px-1.5 py-1 rounded bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 font-medium">
                                +{granted.length - shownChips.length}
                              </span>
                            )}
                          </>
                        )}
                      </span>
                      <span className="flex items-center gap-1 text-[11px] font-medium text-[var(--color-primary)] whitespace-nowrap flex-shrink-0 pt-0.5">
                        {isOpen ? "Zwiń" : "Zarządzaj"}
                        <ChevronDown size={13} className={`transition-transform ${isOpen ? "rotate-180" : ""}`} />
                      </span>
                    </button>
                  </td>
                </tr>

                {isOpen && (
                  <tr className="bg-gray-50 dark:bg-gray-700/30">
                    <td colSpan={5} className="px-4 pb-4 pt-1">
                      <PermissionGrid
                        u={u}
                        isSelf={isSelf}
                        isCEO={isCEO}
                        isDev={isDev}
                        isPending={isPending}
                        ceoConfirm={ceoConfirm}
                        patch={patch}
                      />
                    </td>
                  </tr>
                )}
                </tbody>
              );
            })}
            {filtered.length === 0 && (
              <tbody>
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-sm text-gray-400">
                    {emptyStateText}
                  </td>
                </tr>
              </tbody>
            )}
        </table>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden flex flex-col gap-2">
        {filtered.map((u) => (
          <UserCardMobile
            key={u.id}
            u={u}
            isSelf={u.id === me?.id}
            isCEO={isCEO}
            isDev={isDev}
            isPending={isPending}
            branches={branches}
            ceoConfirm={ceoConfirm}
            patch={patch}
            handleRoleChange={handleRoleChange}
          />
        ))}
        {filtered.length === 0 && (
          <div className="px-4 py-8 text-center text-sm text-gray-400">
            {emptyStateText}
          </div>
        )}
      </div>

      {pendingRoleChange && createPortal(
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-sm p-6 flex flex-col gap-5">

            {/* Icon + header */}
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center">
                <AlertTriangle size={20} className="text-amber-600 dark:text-amber-400" />
              </div>
              <div>
                <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100 leading-snug">
                  Podniesienie uprawnień
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                  Zmiana roli{" "}
                  <span className="font-medium text-gray-800 dark:text-gray-200">
                    {pendingRoleChange.userName}
                  </span>
                </p>
              </div>
            </div>

            {/* Role change visualization */}
            <div className="flex items-center justify-center gap-3 bg-gray-50 dark:bg-gray-700/50 rounded-xl px-4 py-3">
              <span className="px-2.5 py-1 rounded-lg bg-gray-200 dark:bg-gray-600 text-sm font-medium text-gray-700 dark:text-gray-200">
                {getRoleLabel(pendingRoleChange.fromRole)}
              </span>
              <ArrowRight size={16} className="text-amber-500 flex-shrink-0" />
              <span className="px-2.5 py-1 rounded-lg bg-amber-100 dark:bg-amber-900/50 text-sm font-semibold text-amber-800 dark:text-amber-300 ring-1 ring-amber-300 dark:ring-amber-700">
                {getRoleLabel(pendingRoleChange.toRole)}
              </span>
            </div>

            <p className="text-sm text-gray-600 dark:text-gray-400">
              Ta zmiana nada użytkownikowi wyższy poziom dostępu do systemu. Upewnij się, że jest to zamierzone.
            </p>

            {/* Buttons */}
            <div className="flex justify-end gap-2 pt-1">
              <button
                onClick={() => setPendingRoleChange(null)}
                className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                Anuluj
              </button>
              <button
                onClick={confirmRoleChange}
                disabled={isPending}
                className="px-4 py-2 text-sm font-semibold bg-amber-500 hover:bg-amber-600 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isPending ? "Zapisywanie…" : "Potwierdź zmianę"}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

// ── Branch management section ──────────────────────────────────────────────────

function BranchSection() {
  const { data: branches = [], isLoading } = useBranches();
  const { mutate: createBranch, isPending: isCreating } = useCreateBranch();
  const { mutate: updateBranch, isPending: isUpdating } = useUpdateBranch();
  const ceoConfirm = useCEOAction();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState<BranchFormData>({ name: "", street: "", city: "", postal_code: "", is_active: true });
  const [formError, setFormError] = useState<string | null>(null);

  function openEdit(b: Branch) {
    setEditingId(b.id);
    setFormError(null);
    setForm({ name: b.name, street: b.street ?? "", city: b.city ?? "", postal_code: b.postal_code ?? "", is_active: b.is_active });
    setShowNew(false);
  }

  function openNew() {
    setShowNew(true);
    setEditingId(null);
    setFormError(null);
    setForm({ name: "", street: "", city: "", postal_code: "", is_active: true });
  }

  function extractError(err: unknown): string {
    const res = (err as { response?: { status?: number; data?: Record<string, unknown> } })?.response;
    if (!res) return "Błąd sieci. Sprawdź połączenie.";
    const body = res.data ?? {};
    // { data, errors, meta } format
    const errors = body.errors as Record<string, unknown> | null | undefined;
    if (errors?.name) return String((errors.name as string[])[0] ?? errors.name);
    if (errors?.detail) return String(errors.detail);
    // DRF default format
    if (body.detail) return String(body.detail);
    // any field errors
    const firstField = Object.values(errors ?? body).find((v) => v);
    if (firstField) return Array.isArray(firstField) ? String(firstField[0]) : String(firstField);
    return `Błąd ${res.status ?? ""}. Sprawdź dane.`;
  }

  function saveNew() {
    setFormError(null);
    ceoConfirm(
      () => createBranch(form, {
        onSuccess: () => { setShowNew(false); setForm({ name: "", street: "", city: "", postal_code: "", is_active: true }); },
        onError: (err) => setFormError(extractError(err)),
      }),
      `Dodanie nowego oddziału "${form.name}"`,
    );
  }

  function saveEdit() {
    if (!editingId) return;
    setFormError(null);
    ceoConfirm(
      () => updateBranch({ id: editingId!, data: form }, {
        onSuccess: () => { setEditingId(null); setFormError(null); },
        onError: (err) => setFormError(extractError(err)),
      }),
      `Edycja oddziału "${form.name}"`,
    );
  }

  const isPending = isCreating || isUpdating;

  if (isLoading) return <PageLoader />;

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-gray-500 dark:text-gray-400">
        Zarządzaj oddziałami firmy. Każdy pracownik jest przypisany do oddziału, który kontroluje widoczność danych kadrowych.
      </p>

      <div className="flex justify-end">
        <button
          onClick={openNew}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-[var(--color-primary)] text-white text-sm font-medium rounded-lg hover:opacity-90 transition-opacity"
        >
          <Plus size={14} /> Nowy oddział
        </button>
      </div>

      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60">
              <th className="text-left px-4 py-2.5 font-mono text-xs uppercase tracking-widest text-gray-500">Nazwa</th>
              <th className="text-left px-4 py-2.5 font-mono text-xs uppercase tracking-widest text-gray-500">Miasto</th>
              <th className="text-left px-4 py-2.5 font-mono text-xs uppercase tracking-widest text-gray-500">Status</th>
              <th className="px-4 py-2.5" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
            {showNew && (
              <tr className="bg-blue-50/40 dark:bg-blue-900/10">
                <td className="px-4 py-2.5">
                  <input
                    autoFocus
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    placeholder="Nazwa oddziału"
                    className="rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-1 text-sm w-full"
                  />
                </td>
                <td className="px-4 py-2.5">
                  <input
                    value={form.city}
                    onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))}
                    placeholder="Miasto"
                    className="rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-1 text-sm w-full"
                  />
                </td>
                <td className="px-4 py-2.5">
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <input type="checkbox" checked={form.is_active} onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))} className="w-3.5 h-3.5" />
                    <span>Aktywny</span>
                  </label>
                </td>
                <td className="px-4 py-2.5">
                  <div className="flex items-center gap-2">
                    <button onClick={saveNew} disabled={isPending || !form.name} className="p-1.5 rounded bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 hover:bg-green-200 disabled:opacity-50"><Check size={12} /></button>
                    <button onClick={() => { setShowNew(false); setFormError(null); }} className="p-1.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200"><X size={12} /></button>
                  </div>
                  {formError && <p className="text-[10px] text-red-600 mt-1">{formError}</p>}
                </td>
              </tr>
            )}
            {branches.map((b) => (
              <tr key={b.id} className="bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-750">
                <td className="px-4 py-2.5">
                  {editingId === b.id ? (
                    <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className="rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-1 text-sm w-full" />
                  ) : (
                    <span className="font-medium text-gray-800 dark:text-gray-200">{b.name}</span>
                  )}
                </td>
                <td className="px-4 py-2.5">
                  {editingId === b.id ? (
                    <input value={form.city} onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))} placeholder="Miasto" className="rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-1 text-sm w-full" />
                  ) : (
                    <span className="text-gray-500">{b.city || "—"}</span>
                  )}
                </td>
                <td className="px-4 py-2.5">
                  {editingId === b.id ? (
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input type="checkbox" checked={form.is_active} onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))} className="w-3.5 h-3.5" />
                      <span>Aktywny</span>
                    </label>
                  ) : (
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${b.is_active ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400" : "bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400"}`}>
                      {b.is_active ? "Aktywny" : "Nieaktywny"}
                    </span>
                  )}
                </td>
                <td className="px-4 py-2.5">
                  {editingId === b.id ? (
                    <>
                      <div className="flex items-center gap-2">
                        <button onClick={saveEdit} disabled={isPending || !form.name} className="p-1.5 rounded bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 hover:bg-green-200 disabled:opacity-50"><Check size={12} /></button>
                        <button onClick={() => { setEditingId(null); setFormError(null); }} className="p-1.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200"><X size={12} /></button>
                      </div>
                      {formError && <p className="text-[10px] text-red-600 mt-1">{formError}</p>}
                    </>
                  ) : (
                    <button onClick={() => openEdit(b)} className="p-1.5 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"><Pencil size={12} /></button>
                  )}
                </td>
              </tr>
            ))}
            {branches.length === 0 && !showNew && (
              <tr><td colSpan={4} className="px-4 py-8 text-center text-sm text-gray-400">Brak oddziałów</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────────

const TABS: { id: Tab; label: string; shortLabel: string; icon: React.ReactNode }[] = [
  { id: "projects", label: "Dostęp do finansów przedsięwzięć", shortLabel: "Finanse PRZ", icon: <Shield size={14} /> },
  { id: "salaries", label: "Dostęp do kosztów płac", shortLabel: "Koszty płac", icon: <DollarSign size={14} /> },
  { id: "schedule", label: "Wgląd w grafik dyspozycyjności", shortLabel: "Grafik", icon: <CalendarDays size={14} /> },
  { id: "agreements", label: "Dostęp do umów niejawnych z podwykonawcami", shortLabel: "Umowy niejawne", icon: <Lock size={14} /> },
  { id: "internal_costs", label: "Dostęp do kosztów wewnętrznych i podatków", shortLabel: "Koszty wewn.", icon: <BarChart3 size={14} /> },
  { id: "group_finance", label: "Dostęp do panelu GK", shortLabel: "Panel GK", icon: <Globe size={14} /> },
  { id: "dlc", label: "Dostęp do modułów DLC", shortLabel: "DLC", icon: <Boxes size={14} /> },
  { id: "roles", label: "Role użytkowników", shortLabel: "Role", icon: <Users size={14} /> },
  { id: "branches", label: "Oddziały firmy", shortLabel: "Oddziały", icon: <MapPin size={14} /> },
  { id: "companies", label: "Spółki GK", shortLabel: "Spółki", icon: <Building2 size={14} /> },
];

export function CEOAccessPage() {
  const { user: me } = useAuthStore();
  const isCEO = !!me?.is_ceo;
  const visibleTabs = isCEO ? TABS : TABS.filter((t) => t.id === "roles");
  const [tab, setTab] = useState<Tab>(isCEO ? "projects" : "roles");
  const [ceoPending, setCeoPending] = useState<{ action: () => void; description?: string } | null>(null);

  const ceoConfirmFn: CEOConfirmFn = isCEO
    ? (action, description) => setCeoPending({ action, description })
    : (action) => action();

  return (
    <CEOConfirmContext.Provider value={ceoConfirmFn}>
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
          {isCEO ? "Panel CEO — Zarządzanie dostępami" : "Zarządzanie uprawnieniami"}
        </h1>
        <div className="w-8 h-0.5 bg-[var(--color-primary)] mt-1 mb-1" />
        <p className="text-xs text-gray-400 mt-0.5">
          {isCEO
            ? "Widoczny wyłącznie dla Zarządu"
            : "Możesz zarządzać uprawnieniami przetargowymi użytkowników."}
        </p>
      </div>

      {visibleTabs.length > 1 && (
        <div className="flex flex-wrap border-b border-gray-200 dark:border-gray-700 gap-y-1">
          {visibleTabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              title={t.label}
              className={`flex-none whitespace-nowrap flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
                tab === t.id
                  ? "border-[var(--color-primary)] text-[var(--color-primary)]"
                  : "border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
              }`}
            >
              {t.icon}
              <span>{t.shortLabel}</span>
            </button>
          ))}
        </div>
      )}

      {tab === "projects" && <ProjectAccessSection />}
      {tab === "salaries" && <SalaryAccessSection />}
      {tab === "schedule" && <ScheduleAccessSection />}
      {tab === "agreements" && <AgreementAccessSection />}
      {tab === "internal_costs" && <InternalCostAccessSection />}
      {tab === "group_finance" && <GroupFinanceAccessSection />}
      {tab === "dlc" && <DLCAccessSection />}
      {tab === "roles" && <RolesSection />}
      {tab === "branches" && <BranchSection />}
      {tab === "companies" && <CompaniesSection />}
    </div>

    {isCEO && ceoPending && createPortal(
      <div className="fixed inset-0 z-[300] flex items-center justify-center bg-black/50 p-4">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-sm p-6 flex flex-col gap-5">

          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/40 flex items-center justify-center">
              <AlertTriangle size={20} className="text-red-600 dark:text-red-400" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100 leading-snug">
                Uwaga — tryb CEO
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                Ta zmiana zostanie zapisana natychmiast
              </p>
            </div>
          </div>

          {ceoPending.description && (
            <div className="flex items-center gap-2 bg-gray-50 dark:bg-gray-700/50 rounded-xl px-4 py-3">
              <span className="text-sm font-medium text-gray-800 dark:text-gray-200">{ceoPending.description}</span>
            </div>
          )}

          <p className="text-sm text-gray-600 dark:text-gray-400">
            Działasz jako CEO. Upewnij się, że ta zmiana jest zamierzona — nie ma możliwości jej automatycznego cofnięcia.
          </p>

          <div className="flex justify-end gap-2 pt-1">
            <button
              onClick={() => setCeoPending(null)}
              className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              Anuluj
            </button>
            <button
              onClick={() => { ceoPending.action(); setCeoPending(null); }}
              className="px-4 py-2 text-sm font-semibold bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors"
            >
              Wykonaj zmianę
            </button>
          </div>
        </div>
      </div>,
      document.body
    )}
    </CEOConfirmContext.Provider>
  );
}
